// src/limits.ts
var PAGE_DOCUMENT_MAX_BYTES = 64 * 1024;
var PAGE_MAX_SUBPAGES = 32;
var PAGE_MAX_BLOCKS_PER_PAGE = 128;
var PAGE_MAX_BLOCK_TEXT_BYTES = 8 * 1024;
var GUESTBOOK_ENTRY_MAX_CHARS = 500;
var PAGE_SLUG_MAX_CHARS = 64;
var PAGE_TITLE_MAX_CHARS = 200;
var PAGE_LINK_LABEL_MAX_CHARS = 200;
var PAGE_LINK_HREF_MAX_CHARS = 2048;
var PAGE_LINK_GROUP_MAX_CHARS = 200;
var PAGE_MAX_LINKS_PER_BLOCK = 40;
var PAGE_MAX_TOP_EIGHT = 8;
var PAGE_MAX_GALLERY_ITEMS = 20;
var PAGE_SHORT_TEXT_MAX_CHARS = 300;
var PAGE_THEME_FIELD_MAX_CHARS = 32;
var ALLOWED_LINK_SCHEMES = ["http:", "https:"];
var PAGE_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
var MAX_TAG_NAME_CHARS = 30;
var MAX_TAGS_PER_POST = 10;
var COMMUNITY_NAME_MIN_CHARS = 3;
var COMMUNITY_NAME_MAX_CHARS = 32;
var COMMUNITY_NAME_PATTERN = /^[a-z0-9_]{3,32}$/;
var MAX_COMMUNITY_DISPLAY_NAME_CHARS = 80;
var MAX_COMMUNITY_DESCRIPTION_CHARS = 500;
var MAX_COMMUNITY_RULES_BYTES = 4 * 1024;
var MAX_COMMUNITY_MODERATORS = 16;
var MAX_ACTOR_FLAIR_BYTES = 1 * 1024;
var MAX_PINNED_POSTS = 3;
var MAX_QUOTED_POST_NESTING_RENDERED = 1;
var MAX_POST_EDITS_PER_POST = 20;
var MAX_POST_CHARS = 5e3;
var MAX_POST_CHARS_NODE_CEILING = 1e4;
var RATE_LIMITS = Object.freeze({
  /** Reposts/unreposts, per actor, per hour. */
  repostPerHour: 60,
  /** Quotes, per actor, per hour. */
  quotePerHour: 30,
  /** Post edits, per actor, per hour. */
  postEditPerHour: 30,
  /** Community creations, per actor, per day. Also node-capability gated
   * (`NodeService.GetNodeInfo`'s `social_capabilities.can_create_community`). */
  communityCreatePerDay: 2,
  /** Community joins, per actor, per day. */
  communityJoinPerDay: 50,
  /** Community invites sent, per actor, per day. */
  communityInvitePerDay: 20,
  /** Community invites sent, per actor, per community, per hour. */
  communityInvitePerCommunityPerHour: 5,
  /** DMs sent, per actor, per minute. */
  dmSendPerMinute: 20,
  /** DMs sent, per actor, per hour. */
  dmSendPerHour: 300,
  /** Message requests sent, per actor, per hour. */
  messageRequestPerHour: 5,
  /** Message requests sent, per actor, per day. */
  messageRequestPerDay: 20,
  /** Tag mutes, total per actor (not a rate — a ceiling on the mute list itself). */
  tagMuteTotal: 100,
  /** Filter create/update, per actor, per hour (§204). */
  filterCreateOrUpdatePerHour: 30,
  /** Filter list publish/update, per actor, per hour (§204). */
  filterListPublishOrUpdatePerHour: 10,
  /** Filter list subscribe, per actor, per hour (§204). */
  filterListSubscribePerHour: 50,
  /** Label apply, per labeler, per day — deliberate, not automatable (§200.5, §204). */
  labelApplyPerDayPerLabeler: 300,
  /** Appeals filed, per actor, per day (§204). */
  appealFiledPerDay: 5,
  /** Account exports requested, per actor, per day (§204). */
  exportRequestedPerDay: 3,
  /** Account deletion requested/cancelled, per actor, per day (§204). */
  accountDeletionRequestedOrCancelledPerDay: 5
});
var MAX_FILTERS_PER_ACTOR = 50;
var MAX_FILTER_TERMS_PER_FILTER = 20;
var MAX_FILTER_LISTS_PUBLISHED_PER_ACTOR = 10;
var MAX_FILTER_LIST_ENTRIES = 2e3;
var MAX_FILTER_LIST_SUBSCRIPTIONS = 100;
var MAX_FILTER_LIST_EXCEPTIONS_PER_LIST = 200;
var MAX_FILTER_NAME_CHARS = 100;
var MAX_FILTER_TERM_VALUE_CHARS = 200;
var MAX_FILTER_LIST_NAME_CHARS = 64;
var MAX_FILTER_LIST_DISPLAY_NAME_CHARS = 80;
var MAX_FILTER_LIST_DESCRIPTION_CHARS = 500;
var MAX_LABELER_SUBSCRIPTIONS_PER_ACTOR = 50;
var MAX_APPEAL_STATEMENT_CHARS = 2e3;
var ACCOUNT_EXPORT_MAX_READY_ARCHIVES = 1;
var ACCOUNT_EXPORT_EXPIRES_AFTER_DAYS = 7;
var ACCOUNT_DELETION_GRACE_PERIOD_DAYS_DEFAULT = 30;
var APPEAL_WINDOW_DAYS_DEFAULT = 14;

// src/sanitize.ts
var ANSI_ESCAPE_SEQUENCE = /\x1B(?:\[[0-?]*[ -/]*[@-~]|\][^\x07\x1B]*(?:\x07|\x1B\\)|P[^\x1B]*\x1B\\|_[^\x1B]*\x1B\\|[0-~])/g;
var CONTROL_BYTES = /[\x00-\x08\x0B-\x1F\x7F-\x9F]/g;
var BIDI_AND_ZERO_WIDTH_RANGES = [
  [8203, 8207],
  [8234, 8238],
  [8294, 8297],
  [65279, 65279]
];
function codePointRangesToCharClass(ranges) {
  return ranges.map(
    ([start, end]) => start === end ? String.fromCodePoint(start) : `${String.fromCodePoint(start)}-${String.fromCodePoint(end)}`
  ).join("");
}
var BIDI_AND_ZERO_WIDTH = new RegExp(
  `[${codePointRangesToCharClass(BIDI_AND_ZERO_WIDTH_RANGES)}]`,
  "g"
);
function sanitizeText(value, options = {}) {
  let result = value.replace(/\r\n?/g, "\n");
  result = result.replace(ANSI_ESCAPE_SEQUENCE, "");
  result = result.replace(/\t/g, " ");
  result = result.replace(CONTROL_BYTES, "");
  result = result.replace(BIDI_AND_ZERO_WIDTH, "");
  if (options.multiline !== true) {
    result = result.replace(/\n/g, " ");
  }
  return result;
}
var textEncoder = new TextEncoder();
function utf8ByteLength(value) {
  return textEncoder.encode(value).length;
}
function containsUnsafeBytes(value) {
  ANSI_ESCAPE_SEQUENCE.lastIndex = 0;
  CONTROL_BYTES.lastIndex = 0;
  BIDI_AND_ZERO_WIDTH.lastIndex = 0;
  return ANSI_ESCAPE_SEQUENCE.test(value) || CONTROL_BYTES.test(value) || BIDI_AND_ZERO_WIDTH.test(value);
}

// src/cosmetics.ts
var DEFAULT_COSMETIC_CAPS = {
  plain: false,
  highContrast: false,
  reducedMotion: false,
  colorDepth: "truecolor"
};
function avatarFrameToken(frame, caps) {
  if (caps.plain || caps.colorDepth === "none") return "none";
  if (frame === "none") return "none";
  if (caps.highContrast === true) return "border";
  return frame;
}
function popEmphasis(caps) {
  if (caps.plain || caps.reducedMotion) return false;
  if (caps.highContrast === true) return false;
  if (caps.colorDepth === "none") return false;
  return true;
}
function nameTagToken(style, caps) {
  if (caps.plain || caps.colorDepth === "none") return "none";
  if (style === "none") return "none";
  return style;
}
var MOTIFS = ["\u2301", "\u25EB", "\u223F", "\u2726", "\u25CD", "\u292B"];
function deterministicIdentityArt(seed) {
  let hash = 2166136261;
  for (const char of seed.toLowerCase()) {
    const cp = char.codePointAt(0) ?? 0;
    hash ^= cp;
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  const hue = hash % 360;
  const motif = MOTIFS[hash % MOTIFS.length] ?? "\u2301";
  const saturated = 62;
  const lightness = 64;
  return { accent: hslToHex(hue, saturated, lightness), motif };
}
function hslToHex(h, s, l) {
  const sn = s / 100;
  const ln = l / 100;
  const k = (n) => (n + h / 30) % 12;
  const f = (n) => {
    const a = sn * Math.min(ln, 1 - ln);
    return ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  };
  const toByte = (v) => Math.round(v * 255).toString(16).padStart(2, "0");
  return `#${toByte(f(0))}${toByte(f(8))}${toByte(f(4))}`;
}

// src/diagnostics.ts
import { z } from "zod";
var DIAGNOSTICS_BUNDLE_SCHEMA_VERSION = 1;
var MAX_DIAGNOSTICS_BREADCRUMBS = 100;
var DIAGNOSTICS_BREADCRUMB_DETAIL_MAX_CHARS = 200;
var MAX_DIAGNOSTICS_EVENTS = 100;
var DIAGNOSTICS_EVENT_MAX_CHARS = 300;
var MAX_DIAGNOSTICS_CAPABILITIES = 64;
var MAX_DIAGNOSTICS_BUNDLE_BYTES = 256 * 1024;
var DIAGNOSTICS_FRAME_TAIL_MAX_CHARS = 24e3;
var DIAGNOSTICS_SCREENSHOT_MAX_CHARS = 2e5;
var DIAGNOSTICS_NOTES_MAX_CHARS = 2e3;
var PRIVATE_KEY_BLOCK = /-----BEGIN(?: [A-Z0-9]+)* PRIVATE KEY-----[\s\S]*?(?:-----END(?: [A-Z0-9]+)* PRIVATE KEY-----|$)/g;
var JWT = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
var BEARER_CREDENTIAL = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
var PROVIDER_TOKENS = /\b(?:sk|rk|pk)-[A-Za-z0-9_-]{16,}\b|\bgh[pousr]_[A-Za-z0-9]{20,}\b|\bgithub_pat_[A-Za-z0-9_]{22,}\b|\bAKIA[0-9A-Z]{16}\b|\bxox[baprs]-[A-Za-z0-9-]{10,}\b/g;
var SECRET_ASSIGNMENT = /(?<=^|[^A-Za-z0-9_.-])([A-Za-z0-9_.-]*(?:password|passwd|secret|token|api[-_]key|access[-_]key|private[-_]key|authorization|credential|cookie)[A-Za-z0-9_.-]*)(["']?\s*[:=]\s*)[^\n]*/gi;
var LONG_HEX_RUN = /\b[A-Fa-f0-9]{40,}\b/g;
var REPLACEMENT = "[REDACTED]";
function redactDiagnosticsText(value) {
  let text = sanitizeText(value, { multiline: true });
  if (text.length === 0) return "";
  text = text.replace(PRIVATE_KEY_BLOCK, REPLACEMENT);
  text = text.replace(JWT, "[REDACTED_JWT]");
  text = text.replace(PROVIDER_TOKENS, REPLACEMENT);
  text = text.replace(LONG_HEX_RUN, REPLACEMENT);
  text = text.replace(SECRET_ASSIGNMENT, "$1$2[REDACTED]");
  text = text.replace(BEARER_CREDENTIAL, "Bearer [REDACTED]");
  return text;
}
function redactBounded(value, maxChars) {
  return redactDiagnosticsText(value).slice(0, maxChars);
}
function toIsoTimestamp(value) {
  const date = value instanceof Date ? value : new Date(value);
  return (Number.isNaN(date.getTime()) ? /* @__PURE__ */ new Date(0) : date).toISOString();
}
function timestampSchema() {
  return z.union([z.date(), z.string()]).transform(toIsoTimestamp).pipe(z.string());
}
function boundedLineSchema(maxChars) {
  return z.string().transform((value) => redactDiagnosticsText(value)).pipe(z.string().max(maxChars));
}
var diagnosticsBreadcrumbSchema = z.object({
  at: timestampSchema(),
  kind: boundedLineSchema(60),
  detail: boundedLineSchema(DIAGNOSTICS_BREADCRUMB_DETAIL_MAX_CHARS)
}).strict();
var diagnosticsEventSchema = z.object({
  at: timestampSchema(),
  /** Metadata-grade structured log line — RPC name + status code grade, never a body. */
  message: boundedLineSchema(DIAGNOSTICS_EVENT_MAX_CHARS)
}).strict();
var diagnosticsBundleSchema = z.object({
  schemaVersion: z.literal(DIAGNOSTICS_BUNDLE_SCHEMA_VERSION),
  app: z.enum(["tui", "web"]),
  version: boundedLineSchema(100),
  buildSha: boundedLineSchema(64),
  nodeDomain: boundedLineSchema(253),
  /** Empty unless the reporter explicitly opted into attaching their handle. */
  sessionHandle: boundedLineSchema(100),
  capabilities: z.record(z.string().max(60), z.boolean()),
  breadcrumbs: z.array(diagnosticsBreadcrumbSchema).max(MAX_DIAGNOSTICS_BREADCRUMBS),
  events: z.array(diagnosticsEventSchema).max(MAX_DIAGNOSTICS_EVENTS),
  frame: z.string().transform((value) => redactDiagnosticsText(value.slice(-DIAGNOSTICS_FRAME_TAIL_MAX_CHARS))).pipe(z.string()).optional(),
  screenshotDataUrl: z.string().refine(
    (value) => value.startsWith("data:image/png;base64,") && value.length <= DIAGNOSTICS_SCREENSHOT_MAX_CHARS,
    { message: "screenshot must be a size-guarded PNG data URL" }
  ).optional(),
  notes: boundedLineSchema(DIAGNOSTICS_NOTES_MAX_CHARS).optional()
}).strict().refine((bundle) => Object.keys(bundle.capabilities).length <= MAX_DIAGNOSTICS_CAPABILITIES, {
  message: `at most ${String(MAX_DIAGNOSTICS_CAPABILITIES)} capability flags`
});
function lastItems(items, max) {
  return items.length <= max ? [...items] : [...items.slice(items.length - max)];
}
function normalizedCapabilities(capabilities) {
  const result = {};
  if (capabilities !== void 0) {
    for (const [name, value] of Object.entries(capabilities)) {
      if (typeof value !== "boolean") continue;
      if (result[name] !== void 0 || Object.keys(result).length >= MAX_DIAGNOSTICS_CAPABILITIES)
        break;
      result[name] = value;
    }
  }
  return result;
}
function serializedByteLength(bundle) {
  return utf8ByteLength(JSON.stringify(bundle));
}
function fitWithinBudget(bundle) {
  let out = bundle;
  if (serializedByteLength(out) <= MAX_DIAGNOSTICS_BUNDLE_BYTES) return out;
  while (out.events.length > 0 && serializedByteLength(out) > MAX_DIAGNOSTICS_BUNDLE_BYTES) {
    out = { ...out, events: out.events.slice(Math.ceil(out.events.length / 2)) };
  }
  if (serializedByteLength(out) <= MAX_DIAGNOSTICS_BUNDLE_BYTES) return out;
  if (out.frame !== void 0) {
    out = { ...out, frame: void 0 };
    if (serializedByteLength(out) <= MAX_DIAGNOSTICS_BUNDLE_BYTES) return out;
  }
  if (out.screenshotDataUrl !== void 0) {
    out = { ...out, screenshotDataUrl: void 0 };
    if (serializedByteLength(out) <= MAX_DIAGNOSTICS_BUNDLE_BYTES) return out;
  }
  let notes = out.notes;
  while (notes !== void 0 && serializedByteLength({ ...out, notes }) > MAX_DIAGNOSTICS_BUNDLE_BYTES && notes.length > 0) {
    const next = notes.slice(0, Math.max(0, Math.floor(notes.length * 0.8)));
    if (next === notes) {
      notes = void 0;
      break;
    }
    notes = next;
  }
  out = notes === void 0 || notes.length === 0 ? { ...out, notes: void 0 } : { ...out, notes };
  while (out.breadcrumbs.length > 0 && serializedByteLength(out) > MAX_DIAGNOSTICS_BUNDLE_BYTES) {
    out = { ...out, breadcrumbs: out.breadcrumbs.slice(Math.ceil(out.breadcrumbs.length / 2)) };
  }
  return out;
}
function guardedScreenshot(value) {
  if (value === void 0) return void 0;
  return value.startsWith("data:image/png;base64,") && value.length <= DIAGNOSTICS_SCREENSHOT_MAX_CHARS ? value : void 0;
}
function buildDiagnosticsBundle(input) {
  const screenshot = guardedScreenshot(input.screenshotDataUrl);
  const candidate = {
    schemaVersion: DIAGNOSTICS_BUNDLE_SCHEMA_VERSION,
    app: input.app,
    version: redactBounded(input.version, 100),
    buildSha: redactBounded(input.buildSha ?? "", 64),
    nodeDomain: redactBounded(input.nodeDomain, 253),
    sessionHandle: redactBounded(input.sessionHandle ?? "", 100),
    capabilities: normalizedCapabilities(input.capabilities),
    breadcrumbs: lastItems(input.breadcrumbs ?? [], MAX_DIAGNOSTICS_BREADCRUMBS).map(
      (breadcrumb) => ({
        at: toIsoTimestamp(breadcrumb.at),
        kind: redactBounded(breadcrumb.kind, 60),
        detail: redactBounded(breadcrumb.detail, DIAGNOSTICS_BREADCRUMB_DETAIL_MAX_CHARS)
      })
    ),
    events: lastItems(input.events ?? [], MAX_DIAGNOSTICS_EVENTS).map((event) => ({
      at: toIsoTimestamp(event.at),
      message: redactBounded(event.message, DIAGNOSTICS_EVENT_MAX_CHARS)
    })),
    ...input.frame === void 0 ? {} : { frame: input.frame },
    ...screenshot === void 0 ? {} : { screenshotDataUrl: screenshot },
    ...input.notes === void 0 ? {} : { notes: input.notes }
  };
  const parsed = diagnosticsBundleSchema.safeParse(candidate);
  if (!parsed.success) {
    throw new Error(`diagnostics bundle failed validation: ${parsed.error.message}`);
  }
  return fitWithinBudget(parsed.data);
}
function serializeDiagnosticsBundle(bundle) {
  return JSON.stringify(bundle, null, 2);
}

// src/web-vitals.ts
import { z as z2 } from "zod";
var STATIC_ROUTE_SEGMENTS = [
  "",
  "report",
  "login",
  "register",
  "search",
  "notifications",
  "bookmarks",
  "compose",
  "appeals",
  "messages",
  "moderation/log"
];
var PARAM_ROUTES = [
  { pattern: /^p\/[^/]+$/, replacement: "/p/:id" },
  { pattern: /^page\/[^/]+\/[^/]+$/, replacement: "/page/:handle/:slug" },
  { pattern: /^page\/[^/]+$/, replacement: "/page/:handle" },
  { pattern: /^t\/[^/]+$/, replacement: "/t/:tag" },
  { pattern: /^c\/[^/]+$/, replacement: "/c/:id" },
  { pattern: /^messages\/[^/]+$/, replacement: "/messages/:id" },
  { pattern: /^settings(\/[^/]+)?$/, replacement: "/settings/:section" }
];
var FALLBACK_ROUTE_PATTERNS = ["/:handle", "/:unknown"];
function pathToRoutePattern(pathname) {
  const trimmed = pathname.replace(/^\/+|\/+$/g, "");
  if (STATIC_ROUTE_SEGMENTS.includes(trimmed)) return `/${trimmed}`;
  for (const { pattern, replacement } of PARAM_ROUTES) {
    if (pattern.test(trimmed)) return replacement;
  }
  if (trimmed !== "" && !trimmed.includes("/")) return "/:handle";
  return "/:unknown";
}
var WEB_VITALS_ROUTE_PATTERNS = /* @__PURE__ */ new Set([
  ...STATIC_ROUTE_SEGMENTS.map((segment) => `/${segment}`),
  ...PARAM_ROUTES.map(({ replacement }) => replacement),
  ...FALLBACK_ROUTE_PATTERNS
]);
function isKnownWebVitalsRoutePattern(route) {
  return WEB_VITALS_ROUTE_PATTERNS.has(route);
}
var WEB_VITALS_METRIC_NAMES = ["CLS", "INP", "LCP"];
var WEB_VITALS_RATINGS = ["good", "needs-improvement", "poor"];
var WEB_VITALS_NAVIGATION_TYPES = [
  "navigate",
  "reload",
  "back-forward",
  "back-forward-cache",
  "prerender",
  "restore",
  "soft-navigation"
];
var WEB_VITALS_MAX_SAMPLES_PER_PAYLOAD = 20;
var WEB_VITALS_MAX_ID_LENGTH = 64;
var WEB_VITALS_MAX_BUILD_VERSION_LENGTH = 64;
var WEB_VITALS_MAX_METRIC_VALUE = 36e5;
var webVitalsSampleSchema = z2.object({
  name: z2.enum(WEB_VITALS_METRIC_NAMES),
  value: z2.number().finite().min(0).max(WEB_VITALS_MAX_METRIC_VALUE),
  rating: z2.enum(WEB_VITALS_RATINGS),
  id: z2.string().min(1).max(WEB_VITALS_MAX_ID_LENGTH)
});
var webVitalsPayloadSchema = z2.object({
  route: z2.string().refine(isKnownWebVitalsRoutePattern, {
    message: "route is not a known route pattern"
  }),
  navigationType: z2.enum(WEB_VITALS_NAVIGATION_TYPES),
  buildVersion: z2.string().min(1).max(WEB_VITALS_MAX_BUILD_VERSION_LENGTH),
  samples: z2.array(webVitalsSampleSchema).min(1).max(WEB_VITALS_MAX_SAMPLES_PER_PAYLOAD)
});

// src/blocks.ts
import { z as z3 } from "zod";
function shortText(maxChars) {
  return z3.string().transform((value) => sanitizeText(value, { multiline: false }).trim()).pipe(z3.string().max(maxChars));
}
function boundedBody(maxBytes = PAGE_MAX_BLOCK_TEXT_BYTES) {
  return z3.string().transform((value) => sanitizeText(value, { multiline: true })).refine((value) => utf8ByteLength(value) <= maxBytes, {
    message: `must be at most ${String(maxBytes)} bytes`
  });
}
var linkHrefSchema = z3.string().trim().max(PAGE_LINK_HREF_MAX_CHARS).refine((value) => !containsUnsafeBytes(value), {
  message: "href must not contain control characters or escape sequences"
}).refine(
  (value) => {
    try {
      return new URL(value).protocol === "http:" || new URL(value).protocol === "https:";
    } catch {
      return false;
    }
  },
  { message: "href must be a valid http(s) URL" }
);
var actorRefSchema = z3.string().trim().min(2).max(100).refine((value) => !containsUnsafeBytes(value), {
  message: "actor reference must not contain control characters or escape sequences"
}).refine((value) => value.startsWith("@"), { message: "actor reference must start with @" });
var mediaIdSchema = z3.uuid("must be a Patches media id");
var textBlockSchema = z3.object({
  type: z3.literal("Text"),
  body: boundedBody()
}).strict();
var markdownBlockSchema = z3.object({
  type: z3.literal("Markdown"),
  body: boundedBody()
}).strict();
var linksBlockSchema = z3.object({
  type: z3.literal("Links"),
  links: z3.array(
    z3.object({
      label: shortText(PAGE_LINK_LABEL_MAX_CHARS),
      href: linkHrefSchema,
      /** Optional group heading — a link-tree-like collection groups its entries
       * under labelled headings (B-119). Absent for an ungrouped entry (old
       * documents parse unchanged); renderers treat an absent *or blank* group as
       * "no heading" (`shortText` already trims/sanitizes), so a blank group never
       * renders a stray heading. */
      group: shortText(PAGE_LINK_GROUP_MAX_CHARS).optional()
    }).strict()
  ).max(PAGE_MAX_LINKS_PER_BLOCK)
}).strict();
var postsBlockSchema = z3.object({
  type: z3.literal("Posts"),
  limit: z3.number().int().min(1).max(50).optional()
}).strict();
var topEightBlockSchema = z3.object({
  type: z3.literal("TopEight"),
  actors: z3.array(actorRefSchema).max(PAGE_MAX_TOP_EIGHT)
}).strict();
var friendsBlockSchema = z3.object({
  type: z3.literal("Friends"),
  limit: z3.number().int().min(1).max(50).optional()
}).strict();
var guestbookBlockSchema = z3.object({
  type: z3.literal("Guestbook"),
  limit: z3.number().int().min(1).max(100).optional()
}).strict();
var badgesBlockSchema = z3.object({
  type: z3.literal("Badges")
}).strict();
var asciiArtBlockSchema = z3.object({
  type: z3.literal("AsciiArt"),
  art: boundedBody()
}).strict();
var SPACER_SIZES = ["sm", "md", "lg"];
var spacerBlockSchema = z3.object({
  type: z3.literal("Spacer"),
  size: z3.enum(SPACER_SIZES).optional()
}).strict();
var heroBlockSchema = z3.object({
  type: z3.literal("Hero"),
  title: shortText(PAGE_SHORT_TEXT_MAX_CHARS),
  subtitle: shortText(PAGE_SHORT_TEXT_MAX_CHARS).optional()
}).strict();
var nowPlayingBlockSchema = z3.object({
  type: z3.literal("NowPlaying"),
  text: shortText(PAGE_SHORT_TEXT_MAX_CHARS)
}).strict();
var imageBlockSchema = z3.object({
  type: z3.literal("Image"),
  mediaId: mediaIdSchema,
  alt: shortText(PAGE_SHORT_TEXT_MAX_CHARS).optional()
}).strict();
var galleryBlockSchema = z3.object({
  type: z3.literal("Gallery"),
  mediaIds: z3.array(mediaIdSchema).min(1).max(PAGE_MAX_GALLERY_ITEMS),
  caption: shortText(PAGE_SHORT_TEXT_MAX_CHARS).optional()
}).strict();
var BLOCK_SCHEMAS = {
  Text: textBlockSchema,
  Markdown: markdownBlockSchema,
  Image: imageBlockSchema,
  Links: linksBlockSchema,
  Posts: postsBlockSchema,
  Gallery: galleryBlockSchema,
  Friends: friendsBlockSchema,
  TopEight: topEightBlockSchema,
  Guestbook: guestbookBlockSchema,
  NowPlaying: nowPlayingBlockSchema,
  Badges: badgesBlockSchema,
  AsciiArt: asciiArtBlockSchema,
  Spacer: spacerBlockSchema,
  Hero: heroBlockSchema
};
var BLOCK_TYPES = Object.keys(BLOCK_SCHEMAS);
var pageBlockSchema = z3.discriminatedUnion("type", [
  textBlockSchema,
  markdownBlockSchema,
  imageBlockSchema,
  linksBlockSchema,
  postsBlockSchema,
  galleryBlockSchema,
  friendsBlockSchema,
  topEightBlockSchema,
  guestbookBlockSchema,
  nowPlayingBlockSchema,
  badgesBlockSchema,
  asciiArtBlockSchema,
  spacerBlockSchema,
  heroBlockSchema
]);
var lenientPageBlockSchema = z3.discriminatedUnion("type", [
  textBlockSchema.passthrough(),
  markdownBlockSchema.passthrough(),
  imageBlockSchema.passthrough(),
  linksBlockSchema.passthrough(),
  postsBlockSchema.passthrough(),
  galleryBlockSchema.passthrough(),
  friendsBlockSchema.passthrough(),
  topEightBlockSchema.passthrough(),
  guestbookBlockSchema.passthrough(),
  nowPlayingBlockSchema.passthrough(),
  badgesBlockSchema.passthrough(),
  asciiArtBlockSchema.passthrough(),
  spacerBlockSchema.passthrough(),
  heroBlockSchema.passthrough()
]);

// src/ssh/wire.ts
function encodeSshString(value) {
  const bytes = typeof value === "string" ? Buffer.from(value, "utf8") : value;
  const header = Buffer.alloc(4);
  header.writeUInt32BE(bytes.length, 0);
  return Buffer.concat([header, bytes]);
}
function encodeSshStrings(parts) {
  return Buffer.concat(parts.map((part) => encodeSshString(part)));
}
var SshWireError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "SshWireError";
  }
};
var SshReader = class {
  constructor(buffer) {
    this.buffer = buffer;
  }
  buffer;
  offset = 0;
  readString() {
    if (this.offset + 4 > this.buffer.length) {
      throw new SshWireError("truncated ssh wire string header");
    }
    const length = this.buffer.readUInt32BE(this.offset);
    this.offset += 4;
    if (length > this.buffer.length - this.offset) {
      throw new SshWireError("ssh wire string length exceeds buffer");
    }
    const value = this.buffer.subarray(this.offset, this.offset + length);
    this.offset += length;
    return value;
  }
  readUtf8String() {
    return this.readString().toString("utf8");
  }
  get atEnd() {
    return this.offset === this.buffer.length;
  }
  /** Rejects trailing bytes — a signature blob with extra data appended is malformed. */
  expectEnd() {
    if (!this.atEnd) throw new SshWireError("unexpected trailing bytes in ssh wire data");
  }
};

// src/ssh/challenge-blob.ts
var SSH_LOGIN_DOMAIN_SEPARATOR = "patches-ssh-login-v1";
var SSH_ENROLL_DOMAIN_SEPARATOR = "patches-ssh-enroll-v1";
function buildSshChallengeBlob(input) {
  return encodeSshStrings([
    input.domainSeparator,
    input.nodeDomain,
    input.challengeId,
    input.nonce,
    input.fingerprint,
    String(Math.floor(input.expiresAt.getTime() / 1e3))
  ]);
}

// src/page.ts
import { z as z4 } from "zod";

// src/errors.ts
var PageValidationError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PageValidationError";
  }
};
function isPageValidationError(value) {
  return value instanceof PageValidationError;
}

// src/page.ts
var CURRENT_PAGE_SCHEMA_VERSION = 1;
var PAGE_BORDER_STYLES = ["single", "double", "round", "ascii", "none"];
var themeSchema = z4.object({
  accent: shortText(PAGE_THEME_FIELD_MAX_CHARS).optional(),
  background: shortText(PAGE_THEME_FIELD_MAX_CHARS).optional(),
  foreground: shortText(PAGE_THEME_FIELD_MAX_CHARS).optional(),
  border: z4.enum(PAGE_BORDER_STYLES).optional(),
  avatarStyle: shortText(PAGE_THEME_FIELD_MAX_CHARS).optional()
}).strict();
var slugSchema = z4.string().trim().toLowerCase().max(PAGE_SLUG_MAX_CHARS).regex(PAGE_SLUG_PATTERN, "slug must be lowercase letters, digits, and hyphens");
var subPageSchema = z4.object({
  slug: slugSchema,
  title: shortText(PAGE_TITLE_MAX_CHARS),
  blocks: z4.array(pageBlockSchema).max(PAGE_MAX_BLOCKS_PER_PAGE)
}).strict();
var pageDocumentShape = z4.object({
  version: z4.literal(CURRENT_PAGE_SCHEMA_VERSION),
  theme: themeSchema.optional(),
  pages: z4.array(subPageSchema).min(1).max(PAGE_MAX_SUBPAGES)
}).strict();
var pageDocumentSchema = pageDocumentShape.superRefine((doc, ctx) => {
  const seenSlugs = /* @__PURE__ */ new Set();
  doc.pages.forEach((subPage, index) => {
    if (seenSlugs.has(subPage.slug)) {
      ctx.addIssue({
        code: "custom",
        path: ["pages", index, "slug"],
        message: `duplicate sub-page slug "${subPage.slug}"`
      });
    }
    seenSlugs.add(subPage.slug);
  });
  const bytes = utf8ByteLength(JSON.stringify(doc));
  if (bytes > PAGE_DOCUMENT_MAX_BYTES) {
    ctx.addIssue({
      code: "custom",
      path: [],
      message: `document is ${String(bytes)} bytes, exceeding the ${String(PAGE_DOCUMENT_MAX_BYTES)} byte limit`
    });
  }
});
function formatZodError(error) {
  const details = error.issues.map((issue) => {
    const path = issue.path.join(".");
    return path.length === 0 ? issue.message : `${path}: ${issue.message}`;
  }).join("; ");
  return details.length === 0 ? "Invalid page document." : details;
}
function parsePageStrict(input) {
  const result = pageDocumentSchema.safeParse(input);
  if (!result.success) {
    throw new PageValidationError(formatZodError(result.error));
  }
  return result.data;
}
function serializePage(doc) {
  return JSON.stringify(doc);
}
function readString(value) {
  return typeof value === "string" ? value : void 0;
}
function parseBlockLenient(raw) {
  const result = lenientPageBlockSchema.safeParse(raw);
  if (result.success) return result.data;
  const originalType = typeof raw === "object" && raw !== null && "type" in raw && typeof raw.type === "string" ? raw.type : "unknown";
  return { type: "Unknown", originalType };
}
function parseSubPageLenient(raw) {
  if (typeof raw !== "object" || raw === null) return void 0;
  const record = raw;
  const slug = readString(record.slug);
  if (slug === void 0) return void 0;
  const rawBlocks = Array.isArray(record.blocks) ? record.blocks : [];
  return {
    slug: slug.trim().toLowerCase().slice(0, PAGE_SLUG_MAX_CHARS),
    title: (readString(record.title) ?? "").slice(0, PAGE_TITLE_MAX_CHARS),
    blocks: rawBlocks.slice(0, PAGE_MAX_BLOCKS_PER_PAGE).map(parseBlockLenient)
  };
}
function parseThemeLenient(raw) {
  const result = themeSchema.safeParse(raw);
  return result.success ? result.data : void 0;
}
function parsePageLenient(input) {
  if (typeof input !== "object" || input === null) {
    throw new PageValidationError("Page document must be a JSON object.");
  }
  const record = input;
  const version = typeof record.version === "number" ? record.version : void 0;
  if (version === void 0) {
    throw new PageValidationError("Page document is missing a schema version.");
  }
  if (!Array.isArray(record.pages)) {
    throw new PageValidationError('Page document is missing its "pages" array.');
  }
  const pages = record.pages.slice(0, PAGE_MAX_SUBPAGES).map(parseSubPageLenient).filter((subPage) => subPage !== void 0);
  return {
    version,
    theme: parseThemeLenient(record.theme),
    pages
  };
}
function parsePageForRender(input) {
  try {
    const strict = parsePageStrict(input);
    return {
      version: strict.version,
      theme: strict.theme,
      pages: strict.pages
    };
  } catch (error) {
    if (!isPageValidationError(error)) throw error;
    return parsePageLenient(input);
  }
}

// src/e2ee/modes.ts
var CONVERSATION_SECURITY_MODES = ["LEGACY_SERVER_VISIBLE", "E2EE_V1"];
var E2EE_CAPABILITY_STATES = [
  "DISABLED",
  "ISOLATED_TEST_ONLY",
  "EXTERNAL_REVIEW_PENDING",
  "EXPERIMENTAL_CANARY",
  "ENABLED"
];
var E2EE_PROTOCOL_V1 = "patches-e2ee-v1";
var E2EE_GROUP_MAX_MEMBERS = 8;
var E2EE_MAX_ACTIVE_DEVICES_PER_ACTOR = 8;
var E2EE_MAX_DEVICE_ENVELOPES_PER_LOGICAL_MESSAGE = E2EE_GROUP_MAX_MEMBERS * E2EE_MAX_ACTIVE_DEVICES_PER_ACTOR;
var E2EE_ONE_TIME_PREKEY_TARGET = 100;
var E2EE_SIGNED_PREKEY_ROTATION_MS = 7 * 24 * 60 * 60 * 1e3;
var E2EE_REPORT_MAX_SURROUNDING_MESSAGES = 10;
var E2EE_MAX_ENVELOPE_BYTES = 64 * 1024;
var E2eeContractError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "E2eeContractError";
  }
};
function assertImmutableConversationMode(persisted, requested) {
  if (persisted !== requested) {
    throw new E2eeContractError("Conversation security mode is immutable.");
  }
}
function assertConversationModeNegotiation(input) {
  if (input.requestedMode === "LEGACY_SERVER_VISIBLE") return;
  if (input.capabilityState !== "ENABLED") {
    throw new E2eeContractError("E2EE_V1 is not enabled on this node.");
  }
  if (input.participantProtocols.length < 2 || input.participantProtocols.some((protocol) => protocol !== E2EE_PROTOCOL_V1)) {
    throw new E2eeContractError("Every participant device must support E2EE_V1.");
  }
}
function assertE2eeGroupBounds(memberCount, envelopeCount) {
  if (!Number.isInteger(memberCount) || memberCount < 2 || memberCount > E2EE_GROUP_MAX_MEMBERS) {
    throw new E2eeContractError("E2EE group membership is outside the supported bound.");
  }
  if (!Number.isInteger(envelopeCount) || envelopeCount < memberCount || envelopeCount > E2EE_MAX_DEVICE_ENVELOPES_PER_LOGICAL_MESSAGE) {
    throw new E2eeContractError("E2EE device fanout is outside the supported bound.");
  }
}
var E2EE_ONE_TIME_PREKEY_REPLENISH_THRESHOLD = 20;
var E2EE_MAILBOX_MAX_LATENCY_MS = 30 * 24 * 60 * 60 * 1e3;
var E2EE_DEVICE_CERTIFICATE_VERSION = 1;
var E2EE_FRANKING_PROFILE_V1 = "patches-franking-v1";
function assertDeviceSupportsProtocol(supportedProtocolVersions, required = E2EE_PROTOCOL_V1) {
  if (!supportedProtocolVersions.includes(required)) {
    throw new E2eeContractError(`Device does not support ${required}; the send must fail.`);
  }
}
function mayDescribeAsEndToEndEncrypted(mode) {
  return mode === "E2EE_V1";
}
function requiredConversationDisclosure(mode) {
  return mode === "E2EE_V1" ? "End-to-end encrypted. This node cannot read these messages, but it can see who you message and when." : "Not end-to-end encrypted \u2014 this node's operators can read these messages.";
}

// src/e2ee/types.ts
var ED25519_PUBLIC_KEY_BYTES = 32;
var ED25519_SIGNATURE_BYTES = 64;
var X25519_PUBLIC_KEY_BYTES = 32;
var E2EE_DIGEST_BYTES = 32;
function bytesEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
function isZeroBytes(value) {
  for (const byte of value) {
    if (byte !== 0) return false;
  }
  return true;
}

// src/e2ee/control.ts
var E2EE_CONTROL_ENVELOPE_DOMAIN = `${E2EE_PROTOCOL_V1}:control-envelope`;
var E2EE_CONTROL_ENVELOPE_VERSION = 1;
var E2EE_CONTROL_TYPES = [
  "READ_RECEIPT",
  "TYPING_START",
  "TYPING_STOP",
  "EDIT",
  "DELETE"
];
var E2EE_CONTROL_MAX_READ_RECEIPT_IDS = 100;
var E2EE_CONTROL_MAX_EDIT_PLAINTEXT_BYTES = 8 * 1024;
var E2EE_CONTROL_MAX_BYTES = E2EE_MAX_ENVELOPE_BYTES - 4 * 1024;
var E2EE_CONTROL_TYPING_TTL_MS = 3e3;
function assertId(value, label) {
  if (value.length === 0 || value.length > 256) {
    throw new E2eeContractError(`${label} is invalid.`);
  }
}
function assertSafeMs(value, label) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new E2eeContractError(`${label} is invalid.`);
  }
}
function assertControlEnvelopeShape(control) {
  assertSafeMs(control.createdAtMs, "Control created-at");
  switch (control.type) {
    case "READ_RECEIPT": {
      if (control.messageIds.length === 0) {
        throw new E2eeContractError("A read receipt covers at least one logical message.");
      }
      if (control.messageIds.length > E2EE_CONTROL_MAX_READ_RECEIPT_IDS) {
        throw new E2eeContractError(
          `A read receipt covers at most ${String(E2EE_CONTROL_MAX_READ_RECEIPT_IDS)} logical messages.`
        );
      }
      let previous;
      for (const id of control.messageIds) {
        assertId(id, "Read receipt logical message id");
        if (previous !== void 0 && previous >= id) {
          throw new E2eeContractError(
            "Read receipt ids must be strictly ascending and unique; one logical message, one entry."
          );
        }
        previous = id;
      }
      return;
    }
    case "TYPING_START":
    case "TYPING_STOP":
      return;
    case "EDIT": {
      assertId(control.logicalMessageId, "Edit logical message id");
      const bytes = new TextEncoder().encode(control.newPlaintext);
      if (bytes.length === 0) {
        throw new E2eeContractError("An edit carries a non-empty replacement plaintext.");
      }
      if (bytes.length > E2EE_CONTROL_MAX_EDIT_PLAINTEXT_BYTES) {
        throw new E2eeContractError(
          `Edit plaintext exceeds ${String(E2EE_CONTROL_MAX_EDIT_PLAINTEXT_BYTES)} bytes.`
        );
      }
      return;
    }
    case "DELETE":
      assertId(control.logicalMessageId, "Delete logical message id");
      return;
  }
}
function encodeLengthPrefixed(parts) {
  let size = 0;
  for (const part of parts) size += 4 + part.length;
  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  let offset = 0;
  for (const part of parts) {
    view.setUint32(offset, part.length, false);
    offset += 4;
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}
function u8Part(value) {
  const out = new Uint8Array(1);
  out[0] = value;
  return out;
}
function u64Part(value) {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setBigUint64(0, BigInt(value), false);
  return out;
}
function canonicalControlEnvelopeBytes(control) {
  assertControlEnvelopeShape(control);
  const encoder = new TextEncoder();
  const parts = [
    encoder.encode(E2EE_CONTROL_ENVELOPE_DOMAIN),
    u8Part(E2EE_CONTROL_ENVELOPE_VERSION),
    encoder.encode(control.type),
    u64Part(control.createdAtMs)
  ];
  switch (control.type) {
    case "READ_RECEIPT":
      parts.push(u64Part(control.messageIds.length));
      for (const id of control.messageIds) parts.push(encoder.encode(id));
      break;
    case "TYPING_START":
    case "TYPING_STOP":
      break;
    case "EDIT":
      parts.push(encoder.encode(control.logicalMessageId));
      parts.push(encoder.encode(control.newPlaintext));
      break;
    case "DELETE":
      parts.push(encoder.encode(control.logicalMessageId));
      break;
  }
  return encodeLengthPrefixed(parts);
}
function encodeControlEnvelope(control, deps) {
  const envelopeBytes = canonicalControlEnvelopeBytes(control);
  if (envelopeBytes.length > E2EE_CONTROL_MAX_BYTES) {
    throw new E2eeContractError(
      `Control envelope exceeds the ${String(E2EE_CONTROL_MAX_BYTES)}-byte ceiling.`
    );
  }
  return {
    control,
    version: E2EE_CONTROL_ENVELOPE_VERSION,
    envelopeBytes,
    envelopeDigest: deps.digest(envelopeBytes)
  };
}
var PartReader = class {
  constructor(source) {
    this.source = source;
  }
  source;
  #offset = 0;
  part() {
    if (this.#offset + 4 > this.source.length) {
      throw new E2eeContractError("Control envelope is truncated.");
    }
    const length = new DataView(
      this.source.buffer,
      this.source.byteOffset + this.#offset,
      4
    ).getUint32(0, false);
    this.#offset += 4;
    if (this.#offset + length > this.source.length) {
      throw new E2eeContractError("Control envelope is truncated.");
    }
    const out = this.source.slice(this.#offset, this.#offset + length);
    this.#offset += length;
    return out;
  }
  u8Part(label) {
    const part = this.part();
    if (part.length !== 1) throw new E2eeContractError(`${label} must be a 1-byte part.`);
    return part[0] ?? 0;
  }
  u64Part(label) {
    const part = this.part();
    if (part.length !== 8) throw new E2eeContractError(`${label} must be an 8-byte part.`);
    return new DataView(part.buffer, part.byteOffset, 8).getBigUint64(0, false);
  }
  stringPart(label) {
    try {
      return new TextDecoder("utf-8", { fatal: true }).decode(this.part());
    } catch {
      throw new E2eeContractError(`${label} is not valid UTF-8.`);
    }
  }
  end() {
    if (this.#offset !== this.source.length) {
      throw new E2eeContractError("Control envelope has trailing bytes.");
    }
  }
};
function decodeControlEnvelope(bytes) {
  if (bytes.length > E2EE_CONTROL_MAX_BYTES) {
    throw new E2eeContractError(
      `Control envelope exceeds the ${String(E2EE_CONTROL_MAX_BYTES)}-byte ceiling.`
    );
  }
  let control;
  try {
    const reader = new PartReader(bytes);
    const domain = reader.stringPart("Control envelope domain");
    if (domain !== E2EE_CONTROL_ENVELOPE_DOMAIN) {
      throw new E2eeContractError("Control envelope bytes carry a foreign domain separator.");
    }
    const version = reader.u8Part("Control envelope version");
    if (version !== E2EE_CONTROL_ENVELOPE_VERSION) {
      throw new E2eeContractError("Control envelope version is not one v1 defines.");
    }
    const type = reader.stringPart("Control envelope type");
    const createdAtMs = Number(reader.u64Part("Control envelope created-at"));
    if (!Number.isSafeInteger(createdAtMs) || createdAtMs < 0) {
      throw new E2eeContractError("Control envelope created-at is invalid.");
    }
    const controlType = type;
    switch (controlType) {
      case "READ_RECEIPT": {
        const count = reader.u64Part("Read receipt id count");
        if (count > BigInt(Number.MAX_SAFE_INTEGER)) {
          throw new E2eeContractError("Read receipt id count is absurd.");
        }
        const messageIds = [];
        for (let index = 0; index < Number(count); index += 1) {
          messageIds.push(reader.stringPart("Read receipt logical message id"));
        }
        control = { type: "READ_RECEIPT", createdAtMs, messageIds };
        break;
      }
      case "TYPING_START":
      case "TYPING_STOP":
        control = { type: controlType, createdAtMs };
        break;
      case "EDIT":
        control = {
          type: "EDIT",
          createdAtMs,
          logicalMessageId: reader.stringPart("Edit logical message id"),
          newPlaintext: reader.stringPart("Edit replacement plaintext")
        };
        break;
      case "DELETE":
        control = {
          type: "DELETE",
          createdAtMs,
          logicalMessageId: reader.stringPart("Delete logical message id")
        };
        break;
      default:
        throw new E2eeContractError("Control envelope type is not one v1 defines.");
    }
    reader.end();
  } catch (error) {
    if (error instanceof E2eeContractError) throw error;
    throw new E2eeContractError("Control envelope bytes are malformed.");
  }
  assertControlEnvelopeShape(control);
  if (!bytesEqual(canonicalControlEnvelopeBytes(control), bytes)) {
    throw new E2eeContractError("Control envelope bytes are not in canonical form.");
  }
  return control;
}
function assertControlEnvelopeDigest(view, deps) {
  if (view.envelopeDigest.length !== E2EE_DIGEST_BYTES) {
    throw new E2eeContractError(
      `Control envelope digest must be ${String(E2EE_DIGEST_BYTES)} bytes.`
    );
  }
  if (!bytesEqual(deps.digest(view.envelopeBytes), view.envelopeDigest)) {
    throw new E2eeContractError("Control envelope digest does not match its transcript.");
  }
}

// src/e2ee/envelopes.ts
var E2EE_FANOUT_TRANSCRIPT_DOMAIN = `${E2EE_PROTOCOL_V1}:fanout`;
function targetKey(target) {
  return `${target.actorId}\0${target.deviceId}`;
}
function assertEnvelopeShape(envelope, frankingProfile = E2EE_FRANKING_PROFILE_V1) {
  if (envelope.recipientActorId.length === 0 || envelope.recipientDeviceId.length === 0) {
    throw new E2eeContractError("Envelope is missing a recipient actor or device id.");
  }
  if (envelope.encryptedHeader.length === 0) {
    throw new E2eeContractError("Envelope has no encrypted header.");
  }
  if (envelope.ciphertext.length === 0) {
    throw new E2eeContractError("Envelope has no ciphertext.");
  }
  if (frankingProfile === E2EE_FRANKING_PROFILE_V1 && envelope.openingCiphertext.length > 0) {
    throw new E2eeContractError(
      "Envelope carries a separate sealed opening; under patches-franking-v1 the franking opening travels inside the ciphertext."
    );
  }
  if (envelope.ciphertextDigest.length !== E2EE_DIGEST_BYTES) {
    throw new E2eeContractError(
      `Envelope ciphertext digest must be ${String(E2EE_DIGEST_BYTES)} bytes.`
    );
  }
  const total = envelope.encryptedHeader.length + envelope.ciphertext.length + envelope.openingCiphertext.length;
  if (total > E2EE_MAX_ENVELOPE_BYTES) {
    throw new E2eeContractError(
      `Envelope is ${String(total)} bytes, above the ${String(E2EE_MAX_ENVELOPE_BYTES)}-byte limit.`
    );
  }
}
function sortFanoutTargets(targets) {
  return [...targets].sort(
    (a, b) => a.actorId === b.actorId ? a.deviceId < b.deviceId ? -1 : a.deviceId > b.deviceId ? 1 : 0 : a.actorId < b.actorId ? -1 : 1
  );
}
function encodeLengthPrefixed2(parts) {
  let size = 0;
  for (const part of parts) size += 4 + part.length;
  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  let offset = 0;
  for (const part of parts) {
    view.setUint32(offset, part.length, false);
    offset += 4;
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}
function canonicalFanoutTranscript(message) {
  const encoder = new TextEncoder();
  const sorted = [...message.deviceEnvelopes].sort(
    (a, b) => a.recipientActorId === b.recipientActorId ? a.recipientDeviceId < b.recipientDeviceId ? -1 : a.recipientDeviceId > b.recipientDeviceId ? 1 : 0 : a.recipientActorId < b.recipientActorId ? -1 : 1
  );
  const count = new Uint8Array(4);
  new DataView(count.buffer).setUint32(0, sorted.length, false);
  const parts = [
    encoder.encode(E2EE_FANOUT_TRANSCRIPT_DOMAIN),
    encoder.encode(message.frankingProfile),
    message.frankingCommitment,
    count
  ];
  for (const envelope of sorted) {
    parts.push(encoder.encode(envelope.recipientActorId));
    parts.push(encoder.encode(envelope.recipientDeviceId));
    parts.push(envelope.ciphertextDigest);
    parts.push(envelope.openingCiphertext);
  }
  return encodeLengthPrefixed2(parts);
}
function assertFanoutDigest(message, deps) {
  if (message.fanoutDigest.length !== E2EE_DIGEST_BYTES) {
    throw new E2eeContractError(`Fanout digest must be ${String(E2EE_DIGEST_BYTES)} bytes.`);
  }
  if (!bytesEqual(deps.digest(canonicalFanoutTranscript(message)), message.fanoutDigest)) {
    throw new E2eeContractError("Fanout digest does not match the envelopes it covers.");
  }
}
function assertCiphertextDigestsMatchCiphertexts(message, deps) {
  for (const envelope of message.deviceEnvelopes) {
    if (!bytesEqual(deps.digest(envelope.ciphertext), envelope.ciphertextDigest)) {
      throw new E2eeContractError(
        `Declared ciphertext digest for device ${envelope.recipientDeviceId} does not match its ciphertext.`
      );
    }
  }
}
function assertFanoutCovers(message, expectedTargets) {
  const expected = new Set(expectedTargets.map(targetKey));
  if (expected.size !== expectedTargets.length) {
    throw new E2eeContractError("Expected fanout targets contain a duplicate device.");
  }
  if (expected.size === 0) {
    throw new E2eeContractError("A logical message must reach at least one device.");
  }
  if (expected.size > E2EE_MAX_DEVICE_ENVELOPES_PER_LOGICAL_MESSAGE) {
    throw new E2eeContractError(
      `Fanout of ${String(expected.size)} devices exceeds the ${String(E2EE_MAX_DEVICE_ENVELOPES_PER_LOGICAL_MESSAGE)}-device bound.`
    );
  }
  const seen = /* @__PURE__ */ new Set();
  const unexpected = [];
  for (const envelope of message.deviceEnvelopes) {
    assertEnvelopeShape(envelope, message.frankingProfile);
    const key = targetKey({
      actorId: envelope.recipientActorId,
      deviceId: envelope.recipientDeviceId
    });
    if (seen.has(key)) {
      throw new E2eeContractError(
        `Fanout addresses device ${envelope.recipientDeviceId} twice; a device gets exactly one envelope.`
      );
    }
    seen.add(key);
    if (!expected.has(key)) unexpected.push(envelope.recipientDeviceId);
  }
  const missing = [...expected].filter((key) => !seen.has(key)).map((key) => key.split("\0")[1]);
  if (missing.length > 0) {
    throw new E2eeContractError(
      `Fanout omits active device(s) ${missing.join(", ")}; a partial fanout silently excludes a participant.`
    );
  }
  if (unexpected.length > 0) {
    throw new E2eeContractError(`Fanout addresses uncertified device(s) ${unexpected.join(", ")}.`);
  }
}
function assertMembershipEpochCurrent(composed, current) {
  if (composed < 1n || current < 1n) {
    throw new E2eeContractError("Membership epochs start at 1.");
  }
  if (composed > current) {
    throw new E2eeContractError(
      "Message claims a membership epoch this conversation has not reached."
    );
  }
  if (composed !== current) {
    throw new E2eeContractError(
      `Message was composed under membership epoch ${String(composed)} but the conversation is at ${String(current)}; it must be re-composed.`
    );
  }
}
function assertGroupFanoutBounds(memberCount, deviceCount) {
  if (!Number.isInteger(memberCount) || memberCount < 2 || memberCount > E2EE_GROUP_MAX_MEMBERS) {
    throw new E2eeContractError(
      `An E2EE conversation has 2..${String(E2EE_GROUP_MAX_MEMBERS)} members.`
    );
  }
  if (!Number.isInteger(deviceCount) || deviceCount < memberCount || deviceCount > memberCount * E2EE_MAX_ACTIVE_DEVICES_PER_ACTOR) {
    throw new E2eeContractError("Device fanout is outside the bound implied by the membership.");
  }
}
function compareMailboxKeys(a, b) {
  const byTime = a.receivedAt.getTime() - b.receivedAt.getTime();
  if (byTime !== 0) return byTime < 0 ? -1 : 1;
  if (a.envelopeId === b.envelopeId) return 0;
  return a.envelopeId < b.envelopeId ? -1 : 1;
}
function assertMailboxPageOrdering(page, after) {
  let previous = after;
  for (const key of page) {
    if (previous !== null && compareMailboxKeys(previous, key) >= 0) {
      throw new E2eeContractError(
        "Mailbox page is not strictly ascending by (received_at, envelope_id)."
      );
    }
    previous = key;
  }
}

// src/e2ee/groups.ts
var E2EE_GROUP_CONTROL_TRANSCRIPT_DOMAIN = `${E2EE_PROTOCOL_V1}:group-control`;
var E2EE_GROUP_CHANGE_KINDS = ["ADDED", "REMOVED"];
function groupControlGenesisPreviousDigest() {
  return new Uint8Array(E2EE_DIGEST_BYTES);
}
function groupControlGenesisTip() {
  return { epoch: 1n, digest: groupControlGenesisPreviousDigest() };
}
function encodeLengthPrefixed3(parts) {
  let size = 0;
  for (const part of parts) size += 4 + part.length;
  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  let offset = 0;
  for (const part of parts) {
    view.setUint32(offset, part.length, false);
    offset += 4;
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}
function canonicalGroupControlTranscript(fields) {
  if (fields.epoch < 0n || fields.epoch > 0xffffffffffffffffn) {
    throw new E2eeContractError("Group-control epoch is out of range.");
  }
  const encoder = new TextEncoder();
  const epoch = new Uint8Array(8);
  new DataView(epoch.buffer).setBigUint64(0, fields.epoch, false);
  return encodeLengthPrefixed3([
    encoder.encode(E2EE_GROUP_CONTROL_TRANSCRIPT_DOMAIN),
    encoder.encode(fields.conversationId),
    epoch,
    encoder.encode(fields.change),
    encoder.encode(fields.subjectActorId),
    encoder.encode(fields.signerActorId),
    encoder.encode(fields.signerDeviceId),
    fields.previousDigest
  ]);
}
function assertGroupControlShape(event) {
  if (!E2EE_GROUP_CHANGE_KINDS.includes(event.change)) {
    throw new E2eeContractError("Group-control change kind is not one v1 defines.");
  }
  if (event.conversationId.length === 0 || event.subjectActorId.length === 0 || event.signerActorId.length === 0 || event.signerDeviceId.length === 0) {
    throw new E2eeContractError("Group-control event is missing an id it must bind.");
  }
  if (event.epoch < 2n) {
    throw new E2eeContractError("Group-control epochs start at 2; epoch 1 is creation.");
  }
  if (event.digest.length !== E2EE_DIGEST_BYTES) {
    throw new E2eeContractError(`Group-control digest must be ${String(E2EE_DIGEST_BYTES)} bytes.`);
  }
  if (event.previousDigest.length !== E2EE_DIGEST_BYTES) {
    throw new E2eeContractError(
      `Group-control previous digest must be ${String(E2EE_DIGEST_BYTES)} bytes.`
    );
  }
  if (event.deviceSignature.length !== ED25519_SIGNATURE_BYTES) {
    throw new E2eeContractError("Group-control signature must be a 64-byte Ed25519 signature.");
  }
}
function verifyGroupControlSignature(event, signerDeviceSigningKey, deps) {
  if (!bytesEqual(deps.digest(event.eventBytes), event.digest)) {
    throw new E2eeContractError("Group-control digest does not match its transcript.");
  }
  if (!deps.verifier.verifyEd25519({
    publicKey: signerDeviceSigningKey,
    message: event.eventBytes,
    signature: event.deviceSignature
  })) {
    throw new E2eeContractError("Group-control event is not signed by the named device.");
  }
}
function assertGroupControlSucceeds(previous, next) {
  assertGroupControlShape(next);
  if (previous === null) {
    if (next.epoch !== 2n) {
      throw new E2eeContractError("A group-control chain must begin at epoch 2.");
    }
    if (!isZeroBytes(next.previousDigest)) {
      throw new E2eeContractError("The first group-control event must chain from genesis.");
    }
    return;
  }
  if (next.epoch !== previous.epoch + 1n) {
    throw new E2eeContractError(
      `Group-control epoch must advance by exactly 1 (expected ${String(previous.epoch + 1n)}, got ${String(next.epoch)}).`
    );
  }
  if (isZeroBytes(next.previousDigest)) {
    throw new E2eeContractError("Only the first group-control event may chain from genesis.");
  }
  if (!bytesEqual(next.previousDigest, previous.digest)) {
    throw new E2eeContractError("Group-control event does not chain to the previous digest.");
  }
}
function assertGroupSizeWithinBound(memberCount) {
  if (!Number.isInteger(memberCount) || memberCount < 1 || memberCount > E2EE_GROUP_MAX_MEMBERS) {
    throw new E2eeContractError(
      `An E2EE conversation has 1..${String(E2EE_GROUP_MAX_MEMBERS)} members.`
    );
  }
}
function assertGroupControlChain(events) {
  let previous = null;
  for (const event of events) {
    assertGroupControlSucceeds(previous, event);
    previous = { epoch: event.epoch, digest: event.digest };
  }
}
function assertGroupControlFieldsMatchBytes(event) {
  if (!bytesEqual(canonicalGroupControlTranscript(event), event.eventBytes)) {
    throw new E2eeContractError("Group-control fields do not match the signed event bytes.");
  }
}
function deriveGroupMembership(input) {
  const members = new Set(input.from.members);
  let epoch = input.from.epoch;
  let tipDigest = input.from.tipDigest;
  for (const event of input.events) {
    assertGroupControlSucceeds(epoch === 1n ? null : { epoch, digest: tipDigest }, event);
    assertGroupControlFieldsMatchBytes(event);
    const key = input.signerKeyFor(event);
    if (key === void 0) {
      throw new E2eeContractError("Group-control signer has no certified device key.");
    }
    verifyGroupControlSignature(event, key, input.deps);
    if (!members.has(event.signerActorId)) {
      throw new E2eeContractError("Group-control event was signed by a non-member.");
    }
    if (event.change === "ADDED") {
      if (members.has(event.subjectActorId)) {
        throw new E2eeContractError("Group-control event adds an existing member.");
      }
      members.add(event.subjectActorId);
      assertGroupSizeWithinBound(members.size);
    } else {
      if (!members.delete(event.subjectActorId)) {
        throw new E2eeContractError("Group-control event removes a non-member.");
      }
    }
    epoch = event.epoch;
    tipDigest = event.digest;
  }
  return { epoch, members: [...members].sort(), tipDigest };
}
function sameMemberSet(left, right) {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return new Set(left).size === left.length && left.every((actor) => rightSet.has(actor));
}

// src/e2ee/history-transfer.ts
var E2EE_HISTORY_TRANSFER_DOMAIN = `${E2EE_PROTOCOL_V1}:history-transfer`;
var E2EE_HISTORY_TRANSFER_VERSION = 1;
var E2EE_HISTORY_TRANSFER_MAX_ENTRIES = 32;
var E2EE_HISTORY_TRANSFER_MAX_ENTRY_BYTES = 8 * 1024;
var E2EE_HISTORY_TRANSFER_MAX_RECORD_BYTES = E2EE_MAX_ENVELOPE_BYTES - 4 * 1024;
var MAX_U64 = 0xffffffffffffffffn;
function assertBigUint64(value, label) {
  if (value < 0n || value > MAX_U64) {
    throw new E2eeContractError(`${label} is out of range.`);
  }
}
function assertId2(value, label) {
  if (value.length === 0 || value.length > 256) {
    throw new E2eeContractError(`${label} is invalid.`);
  }
}
function assertSafeMs2(value, label) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new E2eeContractError(`${label} is invalid.`);
  }
}
function assertHistoryEntryFields(entry) {
  assertId2(entry.conversationId, "History entry conversation id");
  assertId2(entry.logicalMessageId, "History entry logical message id");
  assertId2(entry.senderActorId, "History entry sender actor id");
  assertId2(entry.senderDeviceId, "History entry sender device id");
  assertBigUint64(entry.membershipEpoch, "History entry membership epoch");
  if (entry.membershipEpoch < 1n) {
    throw new E2eeContractError("History entry membership epochs start at 1.");
  }
  assertSafeMs2(entry.acceptedAtMs, "History entry accepted-at");
  if (entry.plaintext.length === 0) {
    throw new E2eeContractError("History entry plaintext is empty.");
  }
  if (entry.plaintext.length > E2EE_HISTORY_TRANSFER_MAX_ENTRY_BYTES) {
    throw new E2eeContractError(
      `History entry plaintext exceeds ${String(E2EE_HISTORY_TRANSFER_MAX_ENTRY_BYTES)} bytes.`
    );
  }
}
function compareEntries(a, b) {
  if (a.acceptedAtMs !== b.acceptedAtMs) return a.acceptedAtMs < b.acceptedAtMs ? -1 : 1;
  if (a.logicalMessageId === b.logicalMessageId) return 0;
  return a.logicalMessageId < b.logicalMessageId ? -1 : 1;
}
function assertHistoryTransferShape(fields) {
  assertId2(fields.conversationId, "History transfer conversation id");
  assertId2(fields.fromActorId, "History transfer sender actor id");
  assertId2(fields.fromDeviceId, "History transfer sender device id");
  if (fields.entries.length === 0) {
    throw new E2eeContractError("A history transfer carries at least one entry.");
  }
  if (fields.entries.length > E2EE_HISTORY_TRANSFER_MAX_ENTRIES) {
    throw new E2eeContractError(
      `A history transfer carries at most ${String(E2EE_HISTORY_TRANSFER_MAX_ENTRIES)} entries.`
    );
  }
  const seen = /* @__PURE__ */ new Set();
  let previous;
  for (const entry of fields.entries) {
    assertHistoryEntryFields(entry);
    if (entry.conversationId !== fields.conversationId) {
      throw new E2eeContractError(
        "History transfer entry names a conversation other than the batch it rides in."
      );
    }
    if (seen.has(entry.logicalMessageId)) {
      throw new E2eeContractError(
        "History transfer lists one logical message more than once; one original message, one entry."
      );
    }
    seen.add(entry.logicalMessageId);
    if (previous !== void 0 && compareEntries(previous, entry) >= 0) {
      throw new E2eeContractError(
        "History transfer entries must be strictly ascending by (accepted_at, logical_message_id)."
      );
    }
    previous = entry;
  }
}
function encodeLengthPrefixed4(parts) {
  let size = 0;
  for (const part of parts) size += 4 + part.length;
  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  let offset = 0;
  for (const part of parts) {
    view.setUint32(offset, part.length, false);
    offset += 4;
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}
function u8Part2(value) {
  const out = new Uint8Array(1);
  out[0] = value;
  return out;
}
function u64Part2(value) {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setBigUint64(0, value, false);
  return out;
}
function canonicalHistoryTransferTranscript(fields) {
  assertHistoryTransferShape(fields);
  const encoder = new TextEncoder();
  const parts = [
    encoder.encode(E2EE_HISTORY_TRANSFER_DOMAIN),
    u8Part2(E2EE_HISTORY_TRANSFER_VERSION),
    encoder.encode(fields.conversationId),
    encoder.encode(fields.fromActorId),
    encoder.encode(fields.fromDeviceId),
    u64Part2(BigInt(fields.entries.length))
  ];
  for (const entry of fields.entries) {
    parts.push(encoder.encode(entry.conversationId));
    parts.push(encoder.encode(entry.logicalMessageId));
    parts.push(encoder.encode(entry.senderActorId));
    parts.push(encoder.encode(entry.senderDeviceId));
    parts.push(u64Part2(entry.membershipEpoch));
    parts.push(u64Part2(BigInt(entry.acceptedAtMs)));
    parts.push(entry.plaintext);
  }
  return encodeLengthPrefixed4(parts);
}
function encodeHistoryTransfer(fields, deps) {
  const recordBytes = canonicalHistoryTransferTranscript(fields);
  if (recordBytes.length > E2EE_HISTORY_TRANSFER_MAX_RECORD_BYTES) {
    throw new E2eeContractError(
      `History transfer record exceeds the ${String(E2EE_HISTORY_TRANSFER_MAX_RECORD_BYTES)}-byte ceiling.`
    );
  }
  return {
    ...fields,
    version: E2EE_HISTORY_TRANSFER_VERSION,
    recordBytes,
    transferDigest: deps.digest(recordBytes)
  };
}
var PartReader2 = class {
  constructor(source) {
    this.source = source;
  }
  source;
  #offset = 0;
  part() {
    if (this.#offset + 4 > this.source.length) {
      throw new E2eeContractError("History transfer record is truncated.");
    }
    const length = new DataView(
      this.source.buffer,
      this.source.byteOffset + this.#offset,
      4
    ).getUint32(0, false);
    this.#offset += 4;
    if (this.#offset + length > this.source.length) {
      throw new E2eeContractError("History transfer record is truncated.");
    }
    const out = this.source.slice(this.#offset, this.#offset + length);
    this.#offset += length;
    return out;
  }
  u8Part(label) {
    const part = this.part();
    if (part.length !== 1) throw new E2eeContractError(`${label} must be a 1-byte part.`);
    return part[0] ?? 0;
  }
  u64Part(label) {
    const part = this.part();
    if (part.length !== 8) throw new E2eeContractError(`${label} must be an 8-byte part.`);
    return new DataView(part.buffer, part.byteOffset, 8).getBigUint64(0, false);
  }
  stringPart(label) {
    try {
      return new TextDecoder().decode(this.part());
    } catch {
      throw new E2eeContractError(`${label} is not valid UTF-8.`);
    }
  }
  end() {
    if (this.#offset !== this.source.length) {
      throw new E2eeContractError("Recovery-family transcript has trailing bytes.");
    }
  }
};
function decodeHistoryTransfer(bytes, deps) {
  let reader;
  try {
    reader = new PartReader2(bytes);
    const domain = reader.stringPart("History transfer domain");
    if (domain !== E2EE_HISTORY_TRANSFER_DOMAIN) {
      throw new E2eeContractError("History transfer bytes carry a foreign domain separator.");
    }
    const version = reader.u8Part("History transfer version");
    if (version !== E2EE_HISTORY_TRANSFER_VERSION) {
      throw new E2eeContractError("History transfer version is not one v1 defines.");
    }
    const conversationId = reader.stringPart("History transfer conversation id");
    const fromActorId = reader.stringPart("History transfer sender actor id");
    const fromDeviceId = reader.stringPart("History transfer sender device id");
    const entryCount = reader.u64Part("History transfer entry count");
    if (entryCount > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new E2eeContractError("History transfer entry count is absurd.");
    }
    const entries = [];
    for (let index = 0; index < Number(entryCount); index += 1) {
      entries.push({
        conversationId: reader.stringPart("History entry conversation id"),
        logicalMessageId: reader.stringPart("History entry logical message id"),
        senderActorId: reader.stringPart("History entry sender actor id"),
        senderDeviceId: reader.stringPart("History entry sender device id"),
        membershipEpoch: reader.u64Part("History entry membership epoch"),
        acceptedAtMs: Number(reader.u64Part("History entry accepted-at")),
        plaintext: reader.part()
      });
    }
    reader.end();
    return encodeHistoryTransfer({ conversationId, fromActorId, fromDeviceId, entries }, deps);
  } catch (error) {
    if (error instanceof E2eeContractError) throw error;
    throw new E2eeContractError("History transfer bytes are malformed.");
  }
}
function assertHistoryTransferDigest(view, deps) {
  if (view.transferDigest.length !== E2EE_DIGEST_BYTES) {
    throw new E2eeContractError(
      `History transfer digest must be ${String(E2EE_DIGEST_BYTES)} bytes.`
    );
  }
  if (!bytesEqual(deps.digest(view.recordBytes), view.transferDigest)) {
    throw new E2eeContractError("History transfer digest does not match its transcript.");
  }
}

// src/e2ee/recovery.ts
var E2EE_RECOVERY_ARCHIVE_DOMAIN = `${E2EE_PROTOCOL_V1}:recovery-archive`;
var E2EE_RECOVERY_ARCHIVE_VERSION = 1;
var E2EE_RECOVERY_KEY_BYTES = ED25519_PUBLIC_KEY_BYTES;
var E2EE_RECOVERY_ARCHIVE_CONTAINER_MAGIC = "PVEARC";
var E2EE_RECOVERY_ARCHIVE_CONTAINER_VERSION = 1;
var E2EE_RECOVERY_ARCHIVE_SALT_BYTES = 32;
var E2EE_RECOVERY_ARCHIVE_NONCE_BYTES = 24;
var E2EE_RECOVERY_ARCHIVE_HEADER_BYTES = E2EE_RECOVERY_ARCHIVE_CONTAINER_MAGIC.length + 1 + E2EE_RECOVERY_ARCHIVE_SALT_BYTES + E2EE_RECOVERY_ARCHIVE_NONCE_BYTES;
var E2EE_RECOVERY_ARCHIVE_KDF_INFO = "patches-e2ee-v1/recovery-archive/aead-key";
var E2EE_RECOVERY_MAX_CONVERSATIONS = 1e3;
var E2EE_RECOVERY_MAX_HISTORY_ENTRIES = 1e3;
var E2EE_RECOVERY_MAX_SETTINGS_BYTES = 4 * 1024;
var MAX_U642 = 0xffffffffffffffffn;
var RECOVERY_CODE_GROUP = 6;
function assertId3(value, label) {
  if (value.length === 0 || value.length > 256) {
    throw new E2eeContractError(`${label} is invalid.`);
  }
}
function assertFixed(value, bytes, label) {
  if (value.length !== bytes) {
    throw new E2eeContractError(`${label} must be ${String(bytes)} bytes.`);
  }
}
function assertBigUint642(value, label) {
  if (value < 0n || value > MAX_U642) {
    throw new E2eeContractError(`${label} is out of range.`);
  }
}
function recoveryCodeChecksumByte(recoveryKey) {
  if (recoveryKey.length !== E2EE_RECOVERY_KEY_BYTES) {
    throw new E2eeContractError("Recovery key must be exactly 32 bytes.");
  }
  let sum = 0;
  for (const byte of recoveryKey) sum = (sum + byte) % 256;
  return sum;
}
function toHex(bytes) {
  let out = "";
  for (const byte of bytes) out += byte.toString(16).padStart(2, "0");
  return out;
}
function encodeRecoveryCode(recoveryKey) {
  if (recoveryKey.length !== E2EE_RECOVERY_KEY_BYTES) {
    throw new E2eeContractError("Recovery key must be exactly 32 bytes.");
  }
  return toHex(recoveryKey) + recoveryCodeChecksumByte(recoveryKey).toString(16).padStart(2, "0");
}
function groupRecoveryCodeForDisplay(code) {
  return code.match(new RegExp(`.{1,${String(RECOVERY_CODE_GROUP)}}`, "g"))?.join("-") ?? code;
}
function fromHex(normalized) {
  const out = new Uint8Array(normalized.length / 2);
  for (let index = 0; index < out.length; index += 1) {
    out[index] = Number.parseInt(normalized.slice(index * 2, index * 2 + 2), 16);
  }
  return out;
}
function decodeRecoveryCode(code) {
  const normalized = code.toLowerCase().replaceAll("-", "").replaceAll(" ", "").replaceAll(":", "");
  if (!/^[0-9a-f]{66}$/.test(normalized)) {
    throw new E2eeContractError("That is not a recovery code.");
  }
  const key = fromHex(normalized.slice(0, 64));
  const checksum = Number.parseInt(normalized.slice(64, 66), 16);
  if (recoveryCodeChecksumByte(key) !== checksum) {
    throw new E2eeContractError("Recovery code checksum does not match; check for a typo.");
  }
  return key;
}
function encodeLengthPrefixed5(parts) {
  let size = 0;
  for (const part of parts) size += 4 + part.length;
  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  let offset = 0;
  for (const part of parts) {
    view.setUint32(offset, part.length, false);
    offset += 4;
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}
function u8Part3(value) {
  const out = new Uint8Array(1);
  out[0] = value;
  return out;
}
function u32Part(value) {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, value, false);
  return out;
}
function u64Part3(value) {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setBigUint64(0, BigInt(value), false);
  return out;
}
function assertRecoveryArchiveShape(document) {
  assertId3(document.actorId, "Archive actor id");
  if (!Number.isSafeInteger(document.rootGeneration) || document.rootGeneration < 1) {
    throw new E2eeContractError("Archive root generation must be a positive integer.");
  }
  assertFixed(document.rootPrivateKey, E2EE_RECOVERY_KEY_BYTES, "Archive root private key");
  assertFixed(document.rootPublicKey, ED25519_PUBLIC_KEY_BYTES, "Archive root public key");
  assertFixed(document.rootSelfSignature, ED25519_SIGNATURE_BYTES, "Archive root self-signature");
  if (document.rootBytes.length === 0) {
    throw new E2eeContractError("Archive root transcript is empty.");
  }
  if (document.rosterBytes.length === 0) {
    throw new E2eeContractError("Archive roster transcript is empty.");
  }
  assertFixed(document.rosterSignature, ED25519_SIGNATURE_BYTES, "Archive roster signature");
  assertBigUint642(document.rosterSequence, "Archive roster sequence");
  if (document.rosterSequence < 1n) {
    throw new E2eeContractError("Archive roster sequence starts at 1.");
  }
  assertFixed(document.rosterDigest, E2EE_DIGEST_BYTES, "Archive roster digest");
  if (!Number.isSafeInteger(document.createdAtMs) || document.createdAtMs < 0) {
    throw new E2eeContractError("Archive creation time is invalid.");
  }
  if (document.conversations.length > E2EE_RECOVERY_MAX_CONVERSATIONS) {
    throw new E2eeContractError(
      `Archive lists more than ${String(E2EE_RECOVERY_MAX_CONVERSATIONS)} conversations.`
    );
  }
  if (document.history.length > E2EE_RECOVERY_MAX_HISTORY_ENTRIES) {
    throw new E2eeContractError(
      `Archive lists more than ${String(E2EE_RECOVERY_MAX_HISTORY_ENTRIES)} history entries.`
    );
  }
  for (const entry of document.history) assertHistoryEntryFields(entry);
  if (document.settings !== void 0 && document.settings.length > E2EE_RECOVERY_MAX_SETTINGS_BYTES) {
    throw new E2eeContractError(
      `Archive settings exceed ${String(E2EE_RECOVERY_MAX_SETTINGS_BYTES)} bytes.`
    );
  }
}
function canonicalRecoveryArchiveTranscript(document) {
  assertRecoveryArchiveShape(document);
  const encoder = new TextEncoder();
  const parts = [
    encoder.encode(E2EE_RECOVERY_ARCHIVE_DOMAIN),
    u8Part3(E2EE_RECOVERY_ARCHIVE_VERSION),
    encoder.encode(document.actorId),
    u32Part(document.rootGeneration),
    document.rootPrivateKey,
    document.rootPublicKey,
    document.rootBytes,
    document.rootSelfSignature,
    document.rosterBytes,
    document.rosterSignature,
    u64Part3(document.rosterSequence),
    document.rosterDigest,
    u64Part3(document.createdAtMs),
    u32Part(document.conversations.length)
  ];
  for (const conversation of document.conversations) {
    assertId3(conversation.conversationId, "Archive conversation id");
    assertBigUint642(conversation.membershipEpoch, "Archive conversation membership epoch");
    if (conversation.membershipEpoch < 1n) {
      throw new E2eeContractError("Archive conversation membership epochs start at 1.");
    }
    assertFixed(
      conversation.groupControlDigest,
      E2EE_DIGEST_BYTES,
      "Archive conversation group-control digest"
    );
    parts.push(encoder.encode(conversation.conversationId));
    parts.push(u64Part3(conversation.membershipEpoch));
    parts.push(conversation.groupControlDigest);
  }
  parts.push(u32Part(document.history.length));
  for (const entry of document.history) {
    parts.push(encoder.encode(entry.conversationId));
    parts.push(encoder.encode(entry.logicalMessageId));
    parts.push(encoder.encode(entry.senderActorId));
    parts.push(encoder.encode(entry.senderDeviceId));
    parts.push(u64Part3(entry.membershipEpoch));
    parts.push(u64Part3(entry.acceptedAtMs));
    parts.push(entry.plaintext);
  }
  parts.push(document.settings ?? new Uint8Array(0));
  return encodeLengthPrefixed5(parts);
}
function encodeRecoveryArchiveDocument(document, deps) {
  const documentBytes = canonicalRecoveryArchiveTranscript(document);
  return {
    ...document,
    version: E2EE_RECOVERY_ARCHIVE_VERSION,
    documentBytes,
    documentDigest: deps.digest(documentBytes)
  };
}
var PartReader3 = class {
  constructor(source) {
    this.source = source;
  }
  source;
  #offset = 0;
  part() {
    if (this.#offset + 4 > this.source.length) {
      throw new E2eeContractError("Recovery archive transcript is truncated.");
    }
    const length = new DataView(
      this.source.buffer,
      this.source.byteOffset + this.#offset,
      4
    ).getUint32(0, false);
    this.#offset += 4;
    if (this.#offset + length > this.source.length) {
      throw new E2eeContractError("Recovery archive transcript is truncated.");
    }
    const out = this.source.slice(this.#offset, this.#offset + length);
    this.#offset += length;
    return out;
  }
  u8Part(label) {
    const part = this.part();
    if (part.length !== 1) throw new E2eeContractError(`${label} must be a 1-byte part.`);
    return part[0] ?? 0;
  }
  u32Part(label) {
    const part = this.part();
    if (part.length !== 4) throw new E2eeContractError(`${label} must be a 4-byte part.`);
    return new DataView(part.buffer, part.byteOffset, 4).getUint32(0, false);
  }
  u64Part(label) {
    const part = this.part();
    if (part.length !== 8) throw new E2eeContractError(`${label} must be an 8-byte part.`);
    return new DataView(part.buffer, part.byteOffset, 8).getBigUint64(0, false);
  }
  stringPart(label) {
    try {
      return new TextDecoder().decode(this.part());
    } catch {
      throw new E2eeContractError(`${label} is not valid UTF-8.`);
    }
  }
  end() {
    if (this.#offset !== this.source.length) {
      throw new E2eeContractError("Recovery archive transcript has trailing bytes.");
    }
  }
};
function decodeRecoveryArchiveDocument(bytes, deps) {
  try {
    const reader = new PartReader3(bytes);
    const domain = reader.stringPart("Recovery archive domain");
    if (domain !== E2EE_RECOVERY_ARCHIVE_DOMAIN) {
      throw new E2eeContractError("Recovery archive bytes carry a foreign domain separator.");
    }
    const version = reader.u8Part("Recovery archive version");
    if (version !== E2EE_RECOVERY_ARCHIVE_VERSION) {
      throw new E2eeContractError("Recovery archive version is not one v1 defines.");
    }
    const actorId = reader.stringPart("Recovery archive actor id");
    const rootGeneration = reader.u32Part("Recovery archive root generation");
    const rootPrivateKey = reader.part();
    const rootPublicKey = reader.part();
    const rootBytes = reader.part();
    const rootSelfSignature = reader.part();
    const rosterBytes = reader.part();
    const rosterSignature = reader.part();
    const rosterSequence = reader.u64Part("Recovery archive roster sequence");
    const rosterDigest = reader.part();
    const createdAtMs = reader.u64Part("Recovery archive creation time");
    const conversationCount = reader.u32Part("Recovery archive conversation count");
    const conversations = [];
    for (let index = 0; index < conversationCount; index += 1) {
      conversations.push({
        conversationId: reader.stringPart("Recovery conversation id"),
        membershipEpoch: reader.u64Part("Recovery conversation membership epoch"),
        groupControlDigest: reader.part()
      });
    }
    const historyCount = reader.u32Part("Recovery archive history count");
    const history = [];
    for (let index = 0; index < historyCount; index += 1) {
      history.push({
        conversationId: reader.stringPart("Recovery history conversation id"),
        logicalMessageId: reader.stringPart("Recovery history logical message id"),
        senderActorId: reader.stringPart("Recovery history sender actor id"),
        senderDeviceId: reader.stringPart("Recovery history sender device id"),
        membershipEpoch: reader.u64Part("Recovery history membership epoch"),
        acceptedAtMs: Number(reader.u64Part("Recovery history accepted-at")),
        plaintext: reader.part()
      });
    }
    const settingsPart = reader.part();
    reader.end();
    const document = {
      actorId,
      rootGeneration,
      rootPrivateKey,
      rootPublicKey,
      rootBytes,
      rootSelfSignature,
      rosterBytes,
      rosterSignature,
      rosterSequence,
      rosterDigest,
      createdAtMs: Number(createdAtMs),
      conversations,
      history,
      settings: settingsPart.length === 0 ? void 0 : settingsPart
    };
    return encodeRecoveryArchiveDocument(document, deps);
  } catch (error) {
    if (error instanceof E2eeContractError) throw error;
    throw new E2eeContractError("Recovery archive bytes are malformed.");
  }
}
function assertServedRosterAcceptsRestore(archive, served) {
  if (served.sequence < archive.rosterSequence) {
    throw new E2eeContractError(
      `Node serves roster sequence ${String(served.sequence)} below the archive's verified ${String(archive.rosterSequence)}; refusing to restore against a rolled-back chain.`
    );
  }
  if (served.sequence === archive.rosterSequence && !bytesEqual(served.digest, archive.rosterDigest)) {
    throw new E2eeContractError(
      "Node serves a roster digest that forks the archive chain at the same sequence."
    );
  }
}
function assertRestoredDeviceCertificateIsFresh(archiveCreatedAtMs, deviceCertificateCreatedAtMs) {
  if (!Number.isSafeInteger(deviceCertificateCreatedAtMs) || deviceCertificateCreatedAtMs < archiveCreatedAtMs) {
    throw new E2eeContractError(
      "A restored device certificate may not predate the archive it restores from."
    );
  }
}
function planRecoveryRestore(view) {
  assertRecoveryArchiveShape(view);
  return {
    actorId: view.actorId,
    rootGeneration: view.rootGeneration,
    rootPrivateKey: view.rootPrivateKey,
    rootPublicKey: view.rootPublicKey,
    rootBytes: view.rootBytes,
    rootSelfSignature: view.rootSelfSignature,
    roster: {
      bytes: view.rosterBytes,
      signature: view.rosterSignature,
      sequence: view.rosterSequence,
      digest: view.rosterDigest
    },
    conversations: view.conversations,
    history: view.history,
    settings: view.settings
  };
}

// src/e2ee/transcript-domains.ts
var DOMAIN_TRANSCRIPT_DOMAINS = Object.freeze([
  E2EE_FANOUT_TRANSCRIPT_DOMAIN,
  E2EE_GROUP_CONTROL_TRANSCRIPT_DOMAIN,
  E2EE_HISTORY_TRANSFER_DOMAIN,
  E2EE_RECOVERY_ARCHIVE_DOMAIN,
  E2EE_CONTROL_ENVELOPE_DOMAIN,
  E2EE_RECOVERY_ARCHIVE_KDF_INFO
]);

// src/e2ee/certificates.ts
var E2EE_DEVICE_STATUSES = ["ACTIVE", "REVOKED", "EXPIRED"];
function assertLength(label, value, expected) {
  if (value.length !== expected) {
    throw new E2eeContractError(`${label} must be ${String(expected)} bytes.`);
  }
}
function verifyIdentityRoot(root, deps) {
  assertLength("Identity root public key", root.publicKey, ED25519_PUBLIC_KEY_BYTES);
  assertLength("Identity root self-signature", root.selfSignature, ED25519_SIGNATURE_BYTES);
  if (!Number.isInteger(root.generation) || root.generation < 1) {
    throw new E2eeContractError("Identity root generation must be a positive integer.");
  }
  if (root.rootBytes.length === 0) {
    throw new E2eeContractError("Identity root transcript is empty.");
  }
  if (!deps.verifier.verifyEd25519({
    publicKey: root.publicKey,
    message: root.rootBytes,
    signature: root.selfSignature
  })) {
    throw new E2eeContractError("Identity root self-signature does not verify.");
  }
}
function verifyDeviceCertificate(certificate, root, deps) {
  assertLength("Device signing key", certificate.signingPublicKey, ED25519_PUBLIC_KEY_BYTES);
  assertLength("Device agreement key", certificate.agreementPublicKey, X25519_PUBLIC_KEY_BYTES);
  assertLength("Device certificate signature", certificate.rootSignature, ED25519_SIGNATURE_BYTES);
  assertLength("Device certificate digest", certificate.certificateDigest, E2EE_DIGEST_BYTES);
  if (certificate.certificateVersion !== E2EE_DEVICE_CERTIFICATE_VERSION) {
    throw new E2eeContractError("Unsupported device certificate version.");
  }
  if (certificate.actorId !== root.actorId) {
    throw new E2eeContractError("Device certificate names a different actor than its root.");
  }
  if (certificate.rootGeneration !== root.generation) {
    throw new E2eeContractError(
      "Device certificate was signed by a superseded root generation; it does not survive a root rotation."
    );
  }
  if (certificate.deviceId.length === 0) {
    throw new E2eeContractError("Device certificate has no device id.");
  }
  if (!certificate.supportedProtocolVersions.includes(E2EE_PROTOCOL_V1)) {
    throw new E2eeContractError(`Device certificate does not advertise ${E2EE_PROTOCOL_V1}.`);
  }
  if (certificate.expiresAt.getTime() <= certificate.createdAt.getTime()) {
    throw new E2eeContractError("Device certificate expires before it was created.");
  }
  if (certificate.expiresAt.getTime() <= deps.now.getTime()) {
    throw new E2eeContractError("Device certificate has expired.");
  }
  if (bytesEqual(certificate.signingPublicKey, certificate.agreementPublicKey)) {
    throw new E2eeContractError("Device signing and agreement keys must be independent keypairs.");
  }
  if (!deps.decodedMatchesTranscript) {
    throw new E2eeContractError(
      "Decoded device certificate fields disagree with the signed transcript."
    );
  }
  if (!bytesEqual(deps.digest(certificate.certificateBytes), certificate.certificateDigest)) {
    throw new E2eeContractError("Device certificate digest does not match its transcript.");
  }
  if (!deps.verifier.verifyEd25519({
    publicKey: root.publicKey,
    message: certificate.certificateBytes,
    signature: certificate.rootSignature
  })) {
    throw new E2eeContractError("Device certificate is not signed by this actor\u2019s messaging root.");
  }
}
function assertDeviceUsableForSend(certificate) {
  if (certificate.status !== "ACTIVE") {
    throw new E2eeContractError("Device is not active; encrypting to it is not permitted.");
  }
  if (certificate.revokedAt !== null && certificate.revokedAt !== void 0) {
    throw new E2eeContractError("Device is revoked; encrypting to it is not permitted.");
  }
}
var E2EE_IDENTITY_CHANGES = ["NONE", "PLANNED_ROTATION", "UNVERIFIED_RESET"];
function classifyIdentityRootChange(previous, next, deps) {
  if (previous === null) return "NONE";
  if (previous.actorId !== next.actorId) {
    throw new E2eeContractError("Cannot compare identity roots belonging to different actors.");
  }
  if (next.generation === previous.generation && bytesEqual(next.publicKey, previous.publicKey)) {
    return "NONE";
  }
  if (next.generation <= previous.generation) {
    throw new E2eeContractError("Identity root generation went backwards; this is a rollback.");
  }
  const transition = next.previousRootSignature;
  if (transition !== void 0 && deps.verifier.verifyEd25519({
    publicKey: previous.publicKey,
    message: next.rootBytes,
    signature: transition
  })) {
    return "PLANNED_ROTATION";
  }
  return "UNVERIFIED_RESET";
}
function requiresReverification(change) {
  return change !== "NONE";
}

// src/e2ee/roster.ts
function rosterGenesisPreviousDigest() {
  return new Uint8Array(E2EE_DIGEST_BYTES);
}
function assertRosterShape(roster) {
  if (roster.sequence < 1n) {
    throw new E2eeContractError("Roster sequence must start at 1.");
  }
  if (roster.digest.length !== E2EE_DIGEST_BYTES) {
    throw new E2eeContractError(`Roster digest must be ${String(E2EE_DIGEST_BYTES)} bytes.`);
  }
  if (roster.previousDigest.length !== E2EE_DIGEST_BYTES) {
    throw new E2eeContractError(
      `Roster previous digest must be ${String(E2EE_DIGEST_BYTES)} bytes.`
    );
  }
  if (roster.rootSignature.length !== ED25519_SIGNATURE_BYTES) {
    throw new E2eeContractError("Roster signature must be a 64-byte Ed25519 signature.");
  }
  if (roster.sequence === 1n && !isZeroBytes(roster.previousDigest)) {
    throw new E2eeContractError("The first roster must chain from the all-zero digest.");
  }
  if (roster.sequence > 1n && isZeroBytes(roster.previousDigest)) {
    throw new E2eeContractError("Only the first roster may chain from the all-zero digest.");
  }
  const seen = /* @__PURE__ */ new Set();
  let activeCount = 0;
  for (const entry of roster.entries) {
    if (seen.has(entry.deviceId)) {
      throw new E2eeContractError(`Roster lists device ${entry.deviceId} more than once.`);
    }
    seen.add(entry.deviceId);
    if (entry.certificateDigest.length !== E2EE_DIGEST_BYTES) {
      throw new E2eeContractError("Roster entry certificate digest has the wrong length.");
    }
    if (entry.active) {
      activeCount += 1;
      if (entry.revokedAt !== null && entry.revokedAt !== void 0) {
        throw new E2eeContractError(`Roster entry ${entry.deviceId} is both active and revoked.`);
      }
    }
  }
  if (activeCount > E2EE_MAX_ACTIVE_DEVICES_PER_ACTOR) {
    throw new E2eeContractError(
      `A roster may list at most ${String(E2EE_MAX_ACTIVE_DEVICES_PER_ACTOR)} active devices.`
    );
  }
}
function verifyRosterSignature(roster, root, deps) {
  if (roster.actorId !== root.actorId) {
    throw new E2eeContractError("Roster names a different actor than its root.");
  }
  if (roster.rootGeneration !== root.generation) {
    throw new E2eeContractError("Roster was signed by a superseded root generation.");
  }
  if (!bytesEqual(deps.digest(roster.rosterBytes), roster.digest)) {
    throw new E2eeContractError("Roster digest does not match its transcript.");
  }
  if (!deps.verifier.verifyEd25519({
    publicKey: root.publicKey,
    message: roster.rosterBytes,
    signature: roster.rootSignature
  })) {
    throw new E2eeContractError("Roster is not signed by this actor\u2019s messaging root.");
  }
}
function assertRosterSucceeds(previous, next) {
  assertRosterShape(next);
  if (previous === null) {
    if (next.sequence !== 1n) {
      throw new E2eeContractError("A roster chain must begin at sequence 1.");
    }
    return;
  }
  if (next.actorId !== previous.actorId) {
    throw new E2eeContractError("Roster chain switched actors.");
  }
  if (next.sequence !== previous.sequence + 1n) {
    throw new E2eeContractError(
      `Roster sequence must advance by exactly 1 (expected ${String(previous.sequence + 1n)}, got ${String(next.sequence)}).`
    );
  }
  if (!bytesEqual(next.previousDigest, previous.digest)) {
    throw new E2eeContractError("Roster does not chain to the previous roster digest.");
  }
  if (next.rootGeneration < previous.rootGeneration) {
    throw new E2eeContractError("Roster root generation went backwards.");
  }
  const before = new Map(previous.entries.map((entry) => [entry.deviceId, entry]));
  for (const entry of next.entries) {
    const prior = before.get(entry.deviceId);
    if (prior === void 0) continue;
    if (!bytesEqual(prior.certificateDigest, entry.certificateDigest)) {
      throw new E2eeContractError(
        `Device ${entry.deviceId} changed certificate without a new device id.`
      );
    }
    if (entry.active && !prior.active) {
      throw new E2eeContractError(
        `Device ${entry.deviceId} was revoked and cannot be reactivated.`
      );
    }
    before.delete(entry.deviceId);
  }
  const dropped = [...before.keys()];
  if (dropped.length > 0) {
    throw new E2eeContractError(
      `Roster dropped device(s) ${dropped.join(", ")}; a device is marked inactive, never removed.`
    );
  }
}
function assertRosterChain(rosters) {
  let previous = null;
  for (const roster of rosters) {
    assertRosterSucceeds(previous, roster);
    previous = roster;
  }
}
function assertRosterNotRolledBack(highestVerifiedSequence, served) {
  if (served.sequence < highestVerifiedSequence) {
    throw new E2eeContractError(
      `Node served roster sequence ${String(served.sequence)} below the verified ${String(highestVerifiedSequence)}; this is a rollback.`
    );
  }
}
function activeDeviceIds(roster) {
  return roster.entries.filter((entry) => entry.active).map((entry) => entry.deviceId).sort();
}

// src/e2ee/franking.ts
var E2EE_EVIDENCE_VERIFICATION_STATUSES = ["PENDING", "VERIFIED", "UNVERIFIABLE"];
var E2EE_EVIDENCE_FAILURE_CODES = [
  "COMMITMENT_MISMATCH",
  "NODE_TAG_MISMATCH",
  "UNKNOWN_FRANKING_PROFILE",
  "UNKNOWN_KEY_ERA",
  "TRANSCRIPT_MISMATCH"
];
var E2EE_REPORT_MAX_EVIDENCE_ITEMS = E2EE_REPORT_MAX_SURROUNDING_MESSAGES + 1;
function assertReporterConsent(consented) {
  if (!consented) {
    throw new E2eeContractError(
      "Report evidence requires explicit reporter consent; disclosure is never a default."
    );
  }
}
function assertReportEvidenceShape(items) {
  if (items.length === 0) {
    throw new E2eeContractError("Report evidence must contain at least the reported message.");
  }
  if (items.length > E2EE_REPORT_MAX_EVIDENCE_ITEMS) {
    throw new E2eeContractError(
      `Report evidence is limited to ${String(E2EE_REPORT_MAX_EVIDENCE_ITEMS)} messages (the reported message plus ${String(E2EE_REPORT_MAX_SURROUNDING_MESSAGES)} surrounding).`
    );
  }
  const positions = /* @__PURE__ */ new Set();
  for (const item of items) {
    if (!Number.isInteger(item.position) || item.position < 0) {
      throw new E2eeContractError("Report evidence positions must be non-negative integers.");
    }
    if (item.position > E2EE_REPORT_MAX_SURROUNDING_MESSAGES) {
      throw new E2eeContractError(
        `Report evidence position ${String(item.position)} is outside the disclosed window.`
      );
    }
    if (positions.has(item.position)) {
      throw new E2eeContractError(
        `Report evidence has two items at position ${String(item.position)}.`
      );
    }
    positions.add(item.position);
    if (item.logicalMessageId.length === 0) {
      throw new E2eeContractError("Report evidence item has no logical message id.");
    }
    if (item.rosterDigest.length !== E2EE_DIGEST_BYTES) {
      throw new E2eeContractError("Report evidence roster digest has the wrong length.");
    }
    if (item.opening.length === 0 || item.commitment.length === 0) {
      throw new E2eeContractError(
        "Report evidence item is missing its franking commitment or opening."
      );
    }
  }
  if (!positions.has(0)) {
    throw new E2eeContractError("Report evidence must include position 0, the reported message.");
  }
}
function verifyReportEvidence(input, deps) {
  assertReporterConsent(input.reporterConsented);
  assertReportEvidenceShape(input.items);
  let verified = 0;
  let failure;
  for (const item of input.items) {
    if (!deps.acceptedProfiles.includes(item.frankingTag.profile)) {
      failure ??= "UNKNOWN_FRANKING_PROFILE";
      continue;
    }
    if (!deps.knownKeyEras.includes(item.frankingTag.keyEra)) {
      failure ??= "UNKNOWN_KEY_ERA";
      continue;
    }
    if (!bytesEqual(deps.digest(item.envelopeTranscript), item.frankingTag.transcriptDigest)) {
      failure ??= "TRANSCRIPT_MISMATCH";
      continue;
    }
    if (!deps.verifier.verifyCommitment({
      commitment: item.commitment,
      opening: item.opening,
      plaintext: item.disclosedPlaintext,
      context: item.commitmentContext
    })) {
      failure ??= "COMMITMENT_MISMATCH";
      continue;
    }
    if (!deps.verifier.verifyNodeTag({
      tag: item.frankingTag,
      transcript: item.envelopeTranscript
    })) {
      failure ??= "NODE_TAG_MISMATCH";
      continue;
    }
    verified += 1;
  }
  return failure === void 0 ? { status: "VERIFIED", verifiedItemCount: verified, partialContext: true } : {
    status: "UNVERIFIABLE",
    verifiedItemCount: verified,
    failureCode: failure,
    partialContext: true
  };
}
var E2EE_FRANKING_MODERATOR_DISCLOSURE = "This evidence was disclosed by the reporter and verified against what this node accepted. It is not proof to anyone outside this node, and it is only the messages the reporter chose to show.";

// src/feature-flags.ts
var FEATURE_FLAG_DEFINITIONS = Object.freeze([]);
function evaluateFeatureFlags(overrides = /* @__PURE__ */ new Map()) {
  return FEATURE_FLAG_DEFINITIONS.map((definition) => ({
    name: definition.name,
    kind: definition.kind,
    enabled: overrides.get(definition.name) ?? definition.defaultEnabled
  }));
}
export {
  ACCOUNT_DELETION_GRACE_PERIOD_DAYS_DEFAULT,
  ACCOUNT_EXPORT_EXPIRES_AFTER_DAYS,
  ACCOUNT_EXPORT_MAX_READY_ARCHIVES,
  ALLOWED_LINK_SCHEMES,
  APPEAL_WINDOW_DAYS_DEFAULT,
  BLOCK_SCHEMAS,
  BLOCK_TYPES,
  COMMUNITY_NAME_MAX_CHARS,
  COMMUNITY_NAME_MIN_CHARS,
  COMMUNITY_NAME_PATTERN,
  CONVERSATION_SECURITY_MODES,
  CURRENT_PAGE_SCHEMA_VERSION,
  DEFAULT_COSMETIC_CAPS,
  DIAGNOSTICS_BREADCRUMB_DETAIL_MAX_CHARS,
  DIAGNOSTICS_BUNDLE_SCHEMA_VERSION,
  DIAGNOSTICS_EVENT_MAX_CHARS,
  DIAGNOSTICS_FRAME_TAIL_MAX_CHARS,
  DIAGNOSTICS_NOTES_MAX_CHARS,
  DIAGNOSTICS_SCREENSHOT_MAX_CHARS,
  DOMAIN_TRANSCRIPT_DOMAINS,
  E2EE_CAPABILITY_STATES,
  E2EE_CONTROL_ENVELOPE_DOMAIN,
  E2EE_CONTROL_ENVELOPE_VERSION,
  E2EE_CONTROL_MAX_BYTES,
  E2EE_CONTROL_MAX_EDIT_PLAINTEXT_BYTES,
  E2EE_CONTROL_MAX_READ_RECEIPT_IDS,
  E2EE_CONTROL_TYPES,
  E2EE_CONTROL_TYPING_TTL_MS,
  E2EE_DEVICE_CERTIFICATE_VERSION,
  E2EE_DEVICE_STATUSES,
  E2EE_DIGEST_BYTES,
  E2EE_EVIDENCE_FAILURE_CODES,
  E2EE_EVIDENCE_VERIFICATION_STATUSES,
  E2EE_FANOUT_TRANSCRIPT_DOMAIN,
  E2EE_FRANKING_MODERATOR_DISCLOSURE,
  E2EE_FRANKING_PROFILE_V1,
  E2EE_GROUP_CHANGE_KINDS,
  E2EE_GROUP_CONTROL_TRANSCRIPT_DOMAIN,
  E2EE_GROUP_MAX_MEMBERS,
  E2EE_HISTORY_TRANSFER_DOMAIN,
  E2EE_HISTORY_TRANSFER_MAX_ENTRIES,
  E2EE_HISTORY_TRANSFER_MAX_ENTRY_BYTES,
  E2EE_HISTORY_TRANSFER_MAX_RECORD_BYTES,
  E2EE_HISTORY_TRANSFER_VERSION,
  E2EE_IDENTITY_CHANGES,
  E2EE_MAILBOX_MAX_LATENCY_MS,
  E2EE_MAX_ACTIVE_DEVICES_PER_ACTOR,
  E2EE_MAX_DEVICE_ENVELOPES_PER_LOGICAL_MESSAGE,
  E2EE_MAX_ENVELOPE_BYTES,
  E2EE_ONE_TIME_PREKEY_REPLENISH_THRESHOLD,
  E2EE_ONE_TIME_PREKEY_TARGET,
  E2EE_PROTOCOL_V1,
  E2EE_RECOVERY_ARCHIVE_CONTAINER_MAGIC,
  E2EE_RECOVERY_ARCHIVE_CONTAINER_VERSION,
  E2EE_RECOVERY_ARCHIVE_DOMAIN,
  E2EE_RECOVERY_ARCHIVE_HEADER_BYTES,
  E2EE_RECOVERY_ARCHIVE_KDF_INFO,
  E2EE_RECOVERY_ARCHIVE_NONCE_BYTES,
  E2EE_RECOVERY_ARCHIVE_SALT_BYTES,
  E2EE_RECOVERY_ARCHIVE_VERSION,
  E2EE_RECOVERY_KEY_BYTES,
  E2EE_RECOVERY_MAX_CONVERSATIONS,
  E2EE_RECOVERY_MAX_HISTORY_ENTRIES,
  E2EE_RECOVERY_MAX_SETTINGS_BYTES,
  E2EE_REPORT_MAX_EVIDENCE_ITEMS,
  E2EE_REPORT_MAX_SURROUNDING_MESSAGES,
  E2EE_SIGNED_PREKEY_ROTATION_MS,
  E2eeContractError,
  ED25519_PUBLIC_KEY_BYTES,
  ED25519_SIGNATURE_BYTES,
  FEATURE_FLAG_DEFINITIONS,
  GUESTBOOK_ENTRY_MAX_CHARS,
  MAX_ACTOR_FLAIR_BYTES,
  MAX_APPEAL_STATEMENT_CHARS,
  MAX_COMMUNITY_DESCRIPTION_CHARS,
  MAX_COMMUNITY_DISPLAY_NAME_CHARS,
  MAX_COMMUNITY_MODERATORS,
  MAX_COMMUNITY_RULES_BYTES,
  MAX_DIAGNOSTICS_BREADCRUMBS,
  MAX_DIAGNOSTICS_BUNDLE_BYTES,
  MAX_DIAGNOSTICS_CAPABILITIES,
  MAX_DIAGNOSTICS_EVENTS,
  MAX_FILTERS_PER_ACTOR,
  MAX_FILTER_LISTS_PUBLISHED_PER_ACTOR,
  MAX_FILTER_LIST_DESCRIPTION_CHARS,
  MAX_FILTER_LIST_DISPLAY_NAME_CHARS,
  MAX_FILTER_LIST_ENTRIES,
  MAX_FILTER_LIST_EXCEPTIONS_PER_LIST,
  MAX_FILTER_LIST_NAME_CHARS,
  MAX_FILTER_LIST_SUBSCRIPTIONS,
  MAX_FILTER_NAME_CHARS,
  MAX_FILTER_TERMS_PER_FILTER,
  MAX_FILTER_TERM_VALUE_CHARS,
  MAX_LABELER_SUBSCRIPTIONS_PER_ACTOR,
  MAX_PINNED_POSTS,
  MAX_POST_CHARS,
  MAX_POST_CHARS_NODE_CEILING,
  MAX_POST_EDITS_PER_POST,
  MAX_QUOTED_POST_NESTING_RENDERED,
  MAX_TAGS_PER_POST,
  MAX_TAG_NAME_CHARS,
  PAGE_BORDER_STYLES,
  PAGE_DOCUMENT_MAX_BYTES,
  PAGE_LINK_GROUP_MAX_CHARS,
  PAGE_LINK_HREF_MAX_CHARS,
  PAGE_LINK_LABEL_MAX_CHARS,
  PAGE_MAX_BLOCKS_PER_PAGE,
  PAGE_MAX_BLOCK_TEXT_BYTES,
  PAGE_MAX_GALLERY_ITEMS,
  PAGE_MAX_LINKS_PER_BLOCK,
  PAGE_MAX_SUBPAGES,
  PAGE_MAX_TOP_EIGHT,
  PAGE_SHORT_TEXT_MAX_CHARS,
  PAGE_SLUG_MAX_CHARS,
  PAGE_SLUG_PATTERN,
  PAGE_THEME_FIELD_MAX_CHARS,
  PAGE_TITLE_MAX_CHARS,
  PageValidationError,
  RATE_LIMITS,
  SSH_ENROLL_DOMAIN_SEPARATOR,
  SSH_LOGIN_DOMAIN_SEPARATOR,
  SshReader,
  SshWireError,
  WEB_VITALS_MAX_BUILD_VERSION_LENGTH,
  WEB_VITALS_MAX_ID_LENGTH,
  WEB_VITALS_MAX_METRIC_VALUE,
  WEB_VITALS_MAX_SAMPLES_PER_PAYLOAD,
  WEB_VITALS_METRIC_NAMES,
  WEB_VITALS_NAVIGATION_TYPES,
  WEB_VITALS_RATINGS,
  WEB_VITALS_ROUTE_PATTERNS,
  X25519_PUBLIC_KEY_BYTES,
  activeDeviceIds,
  assertCiphertextDigestsMatchCiphertexts,
  assertControlEnvelopeDigest,
  assertControlEnvelopeShape,
  assertConversationModeNegotiation,
  assertDeviceSupportsProtocol,
  assertDeviceUsableForSend,
  assertE2eeGroupBounds,
  assertEnvelopeShape,
  assertFanoutCovers,
  assertFanoutDigest,
  assertGroupControlChain,
  assertGroupControlFieldsMatchBytes,
  assertGroupControlShape,
  assertGroupControlSucceeds,
  assertGroupFanoutBounds,
  assertGroupSizeWithinBound,
  assertHistoryEntryFields,
  assertHistoryTransferDigest,
  assertHistoryTransferShape,
  assertImmutableConversationMode,
  assertMailboxPageOrdering,
  assertMembershipEpochCurrent,
  assertRecoveryArchiveShape,
  assertReportEvidenceShape,
  assertReporterConsent,
  assertRestoredDeviceCertificateIsFresh,
  assertRosterChain,
  assertRosterNotRolledBack,
  assertRosterShape,
  assertRosterSucceeds,
  assertServedRosterAcceptsRestore,
  avatarFrameToken,
  buildDiagnosticsBundle,
  buildSshChallengeBlob,
  bytesEqual,
  canonicalControlEnvelopeBytes,
  canonicalFanoutTranscript,
  canonicalGroupControlTranscript,
  canonicalHistoryTransferTranscript,
  canonicalRecoveryArchiveTranscript,
  classifyIdentityRootChange,
  compareMailboxKeys,
  containsUnsafeBytes,
  decodeControlEnvelope,
  decodeHistoryTransfer,
  decodeRecoveryArchiveDocument,
  decodeRecoveryCode,
  deriveGroupMembership,
  deterministicIdentityArt,
  diagnosticsBreadcrumbSchema,
  diagnosticsBundleSchema,
  diagnosticsEventSchema,
  encodeControlEnvelope,
  encodeHistoryTransfer,
  encodeRecoveryArchiveDocument,
  encodeRecoveryCode,
  encodeSshString,
  encodeSshStrings,
  evaluateFeatureFlags,
  groupControlGenesisPreviousDigest,
  groupControlGenesisTip,
  groupRecoveryCodeForDisplay,
  isKnownWebVitalsRoutePattern,
  isPageValidationError,
  isZeroBytes,
  lenientPageBlockSchema,
  mayDescribeAsEndToEndEncrypted,
  nameTagToken,
  pageBlockSchema,
  parsePageForRender,
  parsePageLenient,
  parsePageStrict,
  pathToRoutePattern,
  planRecoveryRestore,
  popEmphasis,
  recoveryCodeChecksumByte,
  redactDiagnosticsText,
  requiredConversationDisclosure,
  requiresReverification,
  rosterGenesisPreviousDigest,
  sameMemberSet,
  sanitizeText,
  serializeDiagnosticsBundle,
  serializePage,
  sortFanoutTargets,
  toIsoTimestamp,
  utf8ByteLength,
  verifyDeviceCertificate,
  verifyGroupControlSignature,
  verifyIdentityRoot,
  verifyReportEvidence,
  verifyRosterSignature,
  webVitalsPayloadSchema,
  webVitalsSampleSchema
};
//# sourceMappingURL=index.js.map